import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { RescomApiService } from '@app/res-common/services/rescom-api.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CoreBaseApiCommandParams } from '@app/core/models/core-base-api.model';
import { IFilter } from 'powerbi-models';
import { CoreModuleName, EidosIconType, LocalStorageKeys } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosIcon, IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiError, DynamicApiRequestPaginatedParams, DynamicApiResponse, DynamicApiResponsePaginatedModel, IDynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { ReservationServiceData, ReservationServiceOption, ReservationServiceType } from '@app/res-common/models/rescom-service.model';
import { IResVillaAvailabilityParams, IReservationModuleAPIConfiguration, ResVillaAvailability, ReservationUploadBlobResponse } from '@app/res-common/models/rescom.model';
import { DateTime } from 'luxon';
import { ReservationApiBoolean } from '../../res-common/models/res-constant.model';
import { IReservationApiPermission } from '../../res-common/models/reservation-permission.model';
import { GeoTreeSearch, IDynamicMapApiParams, IResApiGetGeoTreeTopicItinerary, ResBkgVisaHealth, ResTopicItinerary, ResVoyageDescription } from '../models/geo-tree.model';
import { IResApiPackagePeopleManageParameters, IResApiPeopleManageParameters, IResApiPeopleParameters, ResPeople, ResPeopleType } from '../models/people.model';
import { BookingGuestAirData, FutureFlightsDetail, FutureFlightsGuest, IResApiFutureFlights, IResApiGetAirFlightParams, IResApiManualFlights, IReservationApiAirLine, IReservationApiAirPnr, IReservationApiBookAirResultParams, IReservationApiChangePnrAirClass, IReservationApiDeleteAirPnrParams, IReservationApiDeleteFutureFlightParams, IReservationApiGetAirPnrHistoryParams, IReservationApiGetAirResultsParams, IReservationApiImportPNRParams, IReservationApiRefreshAirResultPnrsParams, IReservationApiUpdateBookingGuestAirData, ResAirData, ResAirFutureFlight, ResAirInternalFlight, ResAirServiceOption, ResBkgAirDetail, ResFutureFlights, ResRightRezOfficeIdConfig, ReservationAirPnr, ReservationAirPnrHistory, ReservationAirResult, ReservationApiAirLineList, ReservationApiRightRezParams, ReservationApiRightRezResponse } from '../models/res-air.model';
import { IResApiCreateBookingCertificate, IResApiGetBookingCertificateDetail, IResApiImportCertificate, IResApiSearchCertificate, IResApiUpdateBookingCertificate, ResBookingCertificate, ResBookingCertificateDetail, ResCertificateType } from '../models/res-bkg-certificate.model';
import { IReservationApiCreateBkgDocumentParams } from '../models/res-bkg-document.model';
import { IResApiBkgItemsManage, IResBkgItemApiParams, ResBkgGroupedItems, ResBkgItem, ResBkgItemFlightInfo, ResBkgItemOverlay, ResBkgItemOverlayMarginCountry, ResBkgItemOverlayPerGroup, ResBkgItemOverlayPerPax, ResBkgItemTotal } from '../models/res-bkg-item.model';
import { IReservationApiGetBkgNotes, IReservationApiManageBkgNotes, ResBkgNote, ResBkgNoteTouch } from '../models/res-bkg-notes.model';
import { BookingCheckSaleRequest, ResDebugBkgItinerary, ResGuestPreference, BookingSaleRequest, BookingToRequest, IResApiAddVillaOptionParams, IResApiBkgAddMarginOverlay, IResApiBkgAddTemplateItems, IResApiGetBkgDiscountAnalysysParams, IResApiGetBkgJourneyItineraryHistoryDetailsParams, IResApiGetBkgJourneyItineraryHistoryParams, IResApiGetBkgJourneyItineraryParams, IResApiGetBkgSuggestedItems, IResApiGetBkgTopicHistoryDetailsParams, IResApiGetDayByDayAllImagesByCategoryParams, IResApiGetDayByDayImagesParams, IResApiGetEresCalendarAvailabilityParams, IResApiGetExportInvoiceDataParams, IResApiGetOperationalDocumentParams, IResApiManageOperationalDocumentParams, IResApiGetPromoDMCParams, IResApiIntraBookingImportParams, IResApiLinkedBookingManageParams, IResApiManageBkgJourneyItinerary, IResApiManageDayByDayImagesParams, IResApiManageJetSeats, IResApiManageOwnArrangementParams, IResApiParametersQuoteCreate, IResApiSyncIntercompanyBookingParams, IResBookingCreateApiResponse, IResBookingGroup, IResBookingManageConfirmSuiteStatusParams, IResBookingManageSuiteRemarkParams, IResBookingSupplierListForAdvisingReportParams, IResTailorMadeItems, IResValueExistCheckParams, IReservationApiBookingActionCheckParameters, IReservationApiBookingActionParameters, IReservationApiBookingAddPackageParameters, IReservationApiBookingDelete, IReservationApiBookingExternalGifUrlParameters, IReservationApiBookingGetMyActivitiesUrlParameters, IReservationApiBookingRequestParameters, IReservationApiBookingVersionRequestParameters, IReservationApiEditBookingParameters, IReservationApiManageBkgWishlistParams, IReservationApiManageReferralDiscounts, IReservationApiParametersGuest, Market, ResAccountingExportData, ResBkgCommunicationEmail, ResBkgCommunicationEmailTemplate, ResBkgDiscountAnalysys, ResBkgDiscountAnalysysGroup, ResBkgDmc, ResBkgDuration, ResBkgExtraItem, ResBkgIntercompanyList, ResBkgJourneyItinerary, ResBkgJourneyItineraryHistory, ResBkgJourneyItineraryHistoryDates, ResBkgLinked, ResBkgLinks, ResBkgMainDestination, ResBkgOperationalDocGuest, ResBkgOperationalDocServiceOption, ResBkgOperationalDocument, ResBkgProductTypes, ResBkgPromoDMC, ResBkgSalesAssistant, ResBkgSecondarySalesAgent, ResBkgSecondaryTravelAgency, ResBkgSegment, ResBkgSubSegment, ResBkgSuggestedItem, ResBkgTailorMadeParams, ResBkgTailorMadeServiceTypes, ResBkgTemplate, ResBkgTemplateTag, ResBkgTopicHistoryDates, ResBkgTopicHistoryDetail, ResBkgVersionHistory, ResBkgVillaOption, ResBkgVillaService, ResBkgWishlist, ResBooking, ResBookingCancelReason, ResBookingDayByDayAllImages, ResBookingDayByDayImages, ResBookingEresCalendarAvailability, ResBookingEresSearchCamp, ResBookingLanguage, ResBookingMultiVoyage, ResBookingSupplierListForAdvisingReport, ResBookingV2VParams, ResFccCalculation, ResFinancialSplit, ResFinancialSplitMultiCurrency, ResPaymentMilestone, IResManagePaymentMilestoneParams, ReservationBookingMarginality, ResBkgExperienceAdditional, IResApiManageBkgAdditionalExpParams, ResBkgIncludedItems, IResApiManageBkgIncludedItemsParams, ResBkgAllowanceItems, ResMiniPackageBkgList, IResApiResetDayByDayImagesParams, ResDMCDocCheck, IResPayerItemsManageParams, IResApiManageBkgLoyaltyItemsParams, ResBkgLoyaltyRedeemItems, ResBkgLoyaltyAvailableItems, IReservationApiDMCReference, IReservationApiManageDMCReferenceParams, ResEresIntegrationCheck, BookingJourneySupplement, IResApiSaveRoomConfigRoom } from '../models/res-booking.model';
import { IReservationCommissionPayoutType, IReservationCompany, IReservationLoyaltyDiscount, IReservationLoyaltyRedemptionTier, IReservationNoteType, IReservationPriceSetup, IReservationPriceType, IReservationSuiteAmenity } from '../models/res-cached-data.models';
import { IReservationCashReceiptCheckNO, IReservationCashReceiptPaymentReason, ResCashReceipt, ResCashReceiptCashAccount, ReservationCashReceiptPayerGroup } from '../models/res-cash-receipt.model';
import { IReservationApiCreatePurchaseOrderParams, IReservationApiCreatePurchaseOrderResult, IReservationApiGetInvoicePackagesParams, IReservationApiGetMultiBookingParams, ResMultiBookingInvoicePackage, ResMultiBookingItem } from '../models/res-multibooking-invoice.model';
import { IResApiCheckCommissionPayoutBatchParams, IResApiCreateCommissionPayoutBatchParams, IResApiDeleteCommissionPayoutParams, IResApiDmcCommissionItemListRow, IResApiGenerateCommissionPayoutBatchSelfInvoiceParams, IResApiGenerateRemittanceParams, IResApiGetCommissionPayoutAgenciesParams, IResApiGetCommissionPayoutBatchesParams, IResApiGetCommissionPayoutBkgListParams, IResApiGetCommissionPayoutParams, IResApiGetRemittanceParams, IResApiGetRemittanceResponse, IResApiManageCommissionPayoutBatchParams, IResApiOpenCommissionPayoutBatchParams, IResApiSendCommissionPayoutBatchToBcParams, IResApiSendCommissionPayoutBatchToFinanceParams, IResApiSendCommissionPayoutEmailSpecs, IResApiUnlockCommissionPayoutBkgsParams, IResApiUpdateCommissionAmount, IResApiUploadCommissionPayoutBatchPaymentParams, ResBonusCommissionDetail, ResCommissionCompanies, ResCommissionPayout, ResCommissionPayoutAK, ResCommissionPayoutBatch, ResDmcCommission, IResApiDmcCommissionTypeChoiceRow, IResDmcCommissionItemsResult, IResDmcCommissionPayoutResult, IResApiManageDmcCommissionPayoutParams, ResDmcCommissionPayoutAgency, ResDmcCommissionPayoutReceipt } from '../models/res-commission.models';
import { IReservationApiGetCounty, IReservationApiGetState } from '../models/res-country-state-county.models';
import { IResGtyUpdateParams, IResInventoryUpdateParams, IResProductsPaymentSchedulerParams, IReservationApiCruiseInventoryParams, IReservationApiManageConnectedSuite, ResDeck, ReservationCruiseAvailabilityModel, ReservationCruiseInventoryModel, ReservationCruiseSuite, ReservationCruiseSuiteListConnectedSuite, ReservationPackagePricesAndCostsHistory, ResInventoryByCategoryModel, ResInventoryVoyagePackage } from '../models/res-cruise-availability.models';
import { IResApiVoyagesSearchParameters, IResGetCabinOccupancyParams, IReservationApiDeleteCruiseParams, IReservationApiUpdateSuiteData, ResSuitePriceCategory, ReservationCabinOccupancy, ReservationCruise, ReservationCruiseItinerary, ReservationCruiseLight, ReservationCruiseSuiteProfitability } from '../models/res-cruise.models';
import { IReservationApiSaveCustomRequest } from '../models/res-custom-request.models';
import { ResDepositToBookingConversionParams } from '../models/res-deposit.model';
import { ResGoodwillCompany, ResGoodwillDefaultPriceType, ResGoodwillDefaultPrices, ResGoodwillRMTypes } from '../models/res-goodwill-deposit.model';
import { IResGetGuestBookedActivitiesParams, ReservationBookingActivitiesBooked } from '../models/res-guest-my-activity';
import { IReservationHomeActionData, IReservationHomeCategory, IReservationHomeItem, IReservationHomeItemActions } from '../models/res-home.model';
import { IReservationApiDeclineInsurance, ResInsurance } from '../models/res-insurance.model ';
import { IResApiCompareManifestParams, IResApiCompareTwoManifestsParams, IResApiDeleteFullManifestParams, IResApiDeleteManifestParams, IResApiGetManifestParams, IResApiGetSavedFullManifestsParams, IResApiGetSavedManifestsParams, IResApiManifestVoyage, IResApiOpenSavedFullManifestParams, IResApiOpenSavedManifestParams, IResApiSaveFullManifestParams, IResApiSaveManifestParams, IResApiUpdateFullManifestHeaderParams, IResApiUpdateFullManifestParams, IResApiUpdateManifestHeaderParams, IResApiUpdateManifestParams, ResManifest, ResSavedManifestHeader } from '../models/res-manifest.model';
import { IResApiGetPriceSetupBookingListParams, IResApiGetReplaceOptionsHistoryParams, IResApiReplaceAvailability, IResApiReplaceOptionsParams, IResApiReplaceStatus, IReservationAddServiceOptionParams, IReservationApiCloneServices, IReservationApiGetOptions, IReservationApiGetSanctuaryCampAllocations, IReservationApiManageOptionAvailability, IReservationCheckComboAvailabilityParams, IReservationDeleteOptionPackageParams, IReservationEditServiceOptionParams, IReservationGetBkgOptionsParams, IReservationGetBkgServicesParams, IReservationUpdateMessageParams, ResPackageClearingCruise, ResPackageClearingNoCruise, ResPackageClearingVoyage, ResPriceSetupBookingList, ResReplaceHistory, ResSanctuaryCampAgency, ResSanctuaryCampAllocation, ResSanctuaryCampSuiteCategory, IResApiPkgEResAllocation, ResPkgEResAllocation, IResApiPkgEResAllocationManageParams, IResManageMultipleEditItem, IReservationAddExtraOptionParams, IReservationAddGuestToItemParams, IServiceOptionGuest2Room, IResBkgItemChangePriceType } from '../models/res-option.models';
import { IResApiAgenciesSearchParameters, IResApiGuestCheckBookingExistsParams, IResApiGuestCheckBookingExistsResult, IResReferralParameters, IReservationApiAgency, IReservationApiAgent, IReservationApiAgentParameters, IReservationApiDeleteGuestParameters, IReservationApiIndividual, IReservationApiIndividualsParameters, IReservationApiInsertGuestParameters, IReservationApiReplaceGuestParameters, IReservationApiUpdateGuestParameters, ResApiAgenciesSearchType, ResOwner, ReservationAgency, ReservationAgent, ReservationIndividual } from '../models/res-owner.model';
import { IReservationApiPackageEcmData, IReservationApiPackageInfo, IReservationApiPackageInfoParameters, ResServiceEMCDetails } from "../models/res-package-detail.model";
import { BookingCreatedBy, HlTypes, IPackageDayLocationsByProject, IPackageDayProjects, IPackageHighlight, IPackageItineraryDay2AKP, IResApiBkgSaveAsTemplate, IResApiChangeBookingOwnerParams, IResApiDayByDayImportFromWebsite, IResApiGetDayByDayPackagesParams, IResApiGetDayByDayTopicItineraryParams, IResApiGetPackageCostSupplierListParams, IResApiGetPackageReportParams, IResApiGetPackageShipSetupParams, IResApiGetPackageTravelDateGuide, IResApiGetPurcahseOrderParams, IResApiGetTourBookParams, IResApiGuestsManage, IResApiManageDayByDayInclusionsParams, IResApiManageDayByDayItinerary, IResApiManageDayByDayNotesItinerary, IResApiManageDayByDayPackagesParams, IResApiManageDayByDayTopicItinerary, IResApiManagePackagePricesSetup, IResApiManagePackageShipSetupParams, IResApiManagePackageSuiteCategoryStatus, IResApiManagePackageTravelDateRelease, IResApiManageSupplierItemRequestListItem, IResApiManageTopicPackageParams, IResApiPackageClone, IResApiPackageCloneSlave, IResApiPackageCommunicationEmail, IResApiPackageConnectedPackages, IResApiPackageOptionToSuiteCategory, IResApiPackageSupplierRequest, IResApiPackageSwapParams, IResApiSupplierContacts, IResApiSupplierEditRequest, IResApiSupplierRequest, IResManagePackageTravelDateGuide, IResPackageCostBandGetParams, IResPackageCostBandManageParams, IResPackageDisplay, IResPackageSetupSingleSuppManage, IResParamsPackageAllocations, IResPriceNote, IResSearchSmartFilter, IReservationApiBookPackageParams, IReservationApiDeletePackageParams, IReservationApiGetPackageParams, IReservationApiLikedPackageUpdateParams, IReservationApiManagePackageAvailabilityParams, IReservationApiManagePackageDepSchedulingParams, IReservationApiManagePackageItineraryDaysParams, IReservationApiManagePackageLinkedPortsParams, IReservationApiManagePackageLinkedToursParams, IReservationApiManagePackageLinkedVoyagesParams, IReservationApiManagePackageOptionsParams, IReservationApiManagePackageParams, IReservationApiManagePackageTravelDatesParams, IReservationApiManagePackageUpgradeParams, IReservationApiManangePackageCanPolicyParams, IReservationApiPackageConfiguratedPrice, IReservationApiPackageOptionCost, IReservationApiPackageOptionPriceParams, IReservationApiPackageOptionPriceTypeAvailibility, IReservationApiPackageOptionPriceTypeAvailibilityParams, IReservationApiPackageUpgradePriceParams, IReservationApiPricesCloneDatesParams, IReservationApiPricesCloneUpgradeParams, IReservationPackageOptionSpecialItem, MarketingStatus, PackageDayLocation, PackageDayProject, PackageHighlight, PackageHighlightType, PackageItineraryDay2AKP, ResBookingDayByDayNotesSuggestion, ResBookingInclusionsDayByDay, ResBookingNotesDayByDay, ResBookingPackageDayByDay, ResBookingTopicDayByDay, ResCruisePriceType, ResInventoryPackage, ResInventoryPriceType, IResApiGetPkgMasterSlaveParams, IResApiManagePkgMasterSlaveParams, ResPackage, ResPackageAllocation, ResPackageAvailabilityStatus, ResPackageBookingList, ResPackageCancPolicy, ResPackageCategory, ResPackageChangesHistory, ResPackageCommunicationEmail, ResPackageConnectedPackages, ResPackageCostSupplierList, ResPackageDepScheduling, ResPackageDepartureFootnotes, ResPackageDepartureFreeShorex, ResPackageExportListByBooking, ResPackageFootnotes, ResPackageItinerary, ResPackageJetSeat, ResPackageJets, IResApiPackageGetJetSeats, IResApiPackageManageJetSeats, ResPackageMasterSlave, ResPackageOperationSpecialist, ResPackageOptionCost, ResPackageOptionToSuiteCategory, ResPackagePrice, ResPackagePriceHeader, ResPackagePriceSetup, ResPackagePriceValidityPeriod, ResPackageProperty, ResPackagePurchaseOrder, IResPackageInvoicesResult, IResApiGetPackageDmcPriceParams, IResApiManagePackageDmcPriceParams, ResPackageDmcPriceResult, ResPackageShipSetup, ResPackageSingleSupp, ResPackageSuiteConfig, ResPackageSupplierEmailRequestURL, ResPackageSupplierRequestData, ResPackageTopic, ResPackageTravelDateGuide, ResPackageTravelDateRelease, ResPackageType, ResPackageUpgrade, ResPackageUpgradePrice, ResPriceSetupExchangeRate, ResSearchFilterPreferences, ResSearchFilterSmartItem, ResSupplierContact, ResSupplierEditRequestDetails, ReservationBasePackage, ReservationPackageHighlightByType, ReservationPackageHighlightsType, TourBook, ResPackageTags, ResPackageTagItem, IResApiManagePackageTagParams, IResApiManageMiniPackageParams, ResMiniPackage, IResApiGetPackageMiniPackagesParams, ResPackagePriceSetupDropdown, IResApiSyncPkgECMParams, IResApiGetPackageInvoicesParams } from '../models/res-package.model';
import { IResApiManageBookingPayerManagementParams, IReservationApiCreateCashReceiptParams, IResTransferCashParams, ResBookingPayerDetails, ResBookingPayerGroup, ResBookingPayerMngmtGroups, ResBookingPayerMngmtGuests, ResBookingPayerMngmtPayers, ResBookingPayerMngmtRelations, ResBookingPayerMngmtRoles, ResBookingPaymentDetailPayer, ResBookingPaymentDetails, ResMaganeBookingPayerResponse, ResPaymentRefundInfo } from '../models/res-payment.model';
import { ResProductService, ReservationCamp } from '../models/res-product.models';
import { IPromoCombinability, IPromoParams, IResApiManagePromoPackages, IResApiManagePromoProperties, IResApiSearchablePromoProperties, IReservationApiManagePromoActionParams, IReservationApiManagePromoAgenciesParams, IReservationApiManagePromoCompatibilityParams, IReservationApiManagePromoParams, IReservationApiManagePromoVoyagesParams, IReservationBkgPromoItemManagePramaters, IReservationPromoApplyPramaters, ResBonusCommissionType, ResGetPromoBooking, ResPromo, ResPromoActionItems, ResPromoApply, ResPromoCompany, ResPromoCompatibility, ResPromoHeader, ResPromoPackage, ResPromoProperty, ResPromoSuiteCategory } from '../models/res-promo.model';
import { IResCampSelectParams, IResDeckplanInfoParams, IResPackagePricesHistoryParams, IResPackageSearchParams, IResPackageSelectParams, IResPackageSnglSuppParams as IResPackageSingleSuppParams, IResProductServiceSelectParams, IResSuiteDeckplanParams, IResVoyageSearchRequiredParams, ResBargeVoyageParams, ResProductCod } from '../models/res-search.models';
import { IReservationApiCreateNewServiceOrOption, IReservationApiGeotree, ResGroups, ResGuestGroup, ResPayerDepartment, ResPayerGroup, ReservationSetupOption, ReservationSetupServiceOptionInclusiveDetail, ResOandaCurrency, IReservationBookVilla, ReservationServiceBookingReport, ReservationPackageServiceAllocation, ReservationServiceAllocation, ReservationServicePackageListElement, ReservationServiceExperienceListElement, ReservationServiceTC, Circuit, ICircuitManage, IResApiGetServiceOptionDayByDayParams, ResServiceOptionDayByDay, IResApiManageServiceOptionDayByDayParams, ResServiceConnectedList, CircuitType, CircuitLookupItem, CircuitDiscountToService, ICircuitDiscountToServiceManage, ResPayerGroupList } from '../models/res-service.models';
import { ReservationShorexOption } from '../models/res-shorex.model';
import { IReservationApiUserInfo } from '../models/res-user.model';
import { IResVillaAvailabilityGetParams } from '@app/res-common/models/villa.model';
import { IResApiGetHeritageDepositParams, ResHeritageCurrency, ResHeritagePrice, ResHeritageTier } from '../models/res-heritage-deposit.model';
import { IResExpAvailabilityGetParams, ResExpAvailability, ResBkgExperiencesApiParams, ResExperience, ResExperienceOptional, ResBkgExperienceManageChoicesApiParams } from '@app/res-common/models/res-experience.model';
import { ResSupplierCancPolicy, ResSupplierDepScheduling } from '@app/res-setup/models/resadm-service.models';
import { ReservationCommissionApiService } from './reservation-commission-api.service';
import { ReservationPromoApiService } from './reservation-promo-api.service';
import { ReservationManifestApiService } from './reservation-manifest-api.service';
import { ReservationApiCoreService } from './reservation-api-core.service';
import { ReservationPackageApiService } from './reservation-package-api.service';
import { ReservationBookingApiService } from './reservation-booking-api.service';
import { ReservationProductPriceApiService } from './reservation-product-price-api.service';
import { ReservationInventoryApiService } from './reservation-inventory-api.service';
import { ReservationPeopleApiService } from './reservation-people-api.service';
import { ReservationServiceSetupApiService } from './reservation-service-setup-api.service';
import { ReservationAirApiService } from './reservation-air-api.service';
import { ReservationSupplierApiService } from './reservation-supplier-api.service';
import { ReservationCashApiService } from './reservation-cash-api.service';
import { ReservationVillaApiService } from './reservation-villa-api.service';
import { ReservationInsuranceApiService } from './reservation-insurance-api.service';
import { IReservationApiUploadBlobParameters, IReservationApiTsUpserGuest, IResVillaEditPrice, IResRoomAvailabilityCheckParams } from './reservation-api.interfaces';
export {
  IReservationErrorApi,
  IReservationApiUploadBlobParameters,
  IReservationApiTsUpserGuest,
  IResVillaEditPrice,
  IResRoomAvailabilityCheckParams,
} from './reservation-api.interfaces';

export interface IResExtendBookingApiParams {
  BkgID: number
  Guests: IReservationApiParametersGuest[]
  BookVilla: IReservationBookVilla[]
}
export interface IResExtendBookingParams extends IResExtendBookingApiParams {
  productCode: string
}

@Injectable({
  providedIn: 'root'
})
export class ReservationApiService extends EidosBaseApiService {

  readonly self = this;

  private get rescomApiService(): RescomApiService {
    return this.injector.get(RescomApiService);
  }

  constructor(
    private readonly commissionApi: ReservationCommissionApiService,
    private readonly promoApi: ReservationPromoApiService,
    private readonly manifestApi: ReservationManifestApiService,
    private readonly coreApi: ReservationApiCoreService,
    private readonly packageApi: ReservationPackageApiService,
    private readonly bookingApi: ReservationBookingApiService,
    private readonly productPriceApi: ReservationProductPriceApiService,
    private readonly inventoryApi: ReservationInventoryApiService,
    private readonly peopleApi: ReservationPeopleApiService,
    private readonly serviceSetupApi: ReservationServiceSetupApiService,
    private readonly airApi: ReservationAirApiService,
    private readonly supplierApi: ReservationSupplierApiService,
    private readonly cashApi: ReservationCashApiService,
    private readonly villaApi: ReservationVillaApiService,
    private readonly insuranceApi: ReservationInsuranceApiService,

    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService,
    private injector: Injector) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);

    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);

        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }
  isEmpty(val:any) {
    if(val===undefined || val===null) return true
    return false
  }
  /**
   * Get current user info
   *
   * @return {*}  {Observable<IReservationApiUserInfo>}
   * @memberof ReservationApiService
   */
  public getUserInfo(): Observable<IReservationApiUserInfo | undefined> {
    return this.callDynamicAPI('account/resuserinfo', {}, 'GET').pipe(
      map<DynamicApiResponse, IReservationApiUserInfo | undefined>((response) =>
        Array.isArray(response.body) && response.body.length > 0 ? (response.body[0] as IReservationApiUserInfo) : undefined)
    );
  }
  public getJwtToken() {
    return this.coreApi.getJwtToken();
  }

  clearApiCache() {
    return this.peopleApi.clearApiCache();
  }
  /**
   * upload file on blob Azure
   *
   * @param {IReservationApiUploadBlobParameters} data*
   * @return {*}  {Observable<ReservationUploadBlobResponse>}
   * @memberof ReservationApiService
   */
  public uploadBlobFile(data: IReservationApiUploadBlobParameters): Observable<ReservationUploadBlobResponse> {
    const formData = new FormData();
    formData.append("fileData", data.BlobFile);

    if (data.UseNewApi) {
      return this.sendFileRequestNew({
        type: "POST",
        url: `documents/document-blob-nocs?Source=${data.Source}&BlobCode=${data.BlobCode}`,
        data: formData,
        reportProgress: false
      })
        .pipe(
          filter((response: any) => response.type !== 0),
          map<any, ReservationUploadBlobResponse>(response => new ReservationUploadBlobResponse(response)),
          catchError(
            (
              error: HttpErrorResponse) => {
              return this.handleGenericError(error);
            }
          )
        );
    } else {
      return this.sendFileRequest({
        type: "POST",
        url: `documents/document-blob?Source=${data.Source}&BlobCode=${data.BlobCode}`,
        data: formData,
        reportProgress: false
      })
        .pipe(
          filter((response: any) => response.type !== 0),
          map<any, ReservationUploadBlobResponse>(response => new ReservationUploadBlobResponse(response)),
          catchError(
            (
              error: HttpErrorResponse) => {
              return this.handleGenericError(error);
            }
          )
        );
    }
  }
  /**
   * upload file on blob Azure
   *
   * @param {IReservationApiUploadBlobParameters} data*
   * @memberof ReservationApiService
   */
  public downBlobFile(guid: string) {
    return this.airApi.downBlobFile(guid);
  }

  public downloadBlobFileInline(guid: string) {
    return this.bookingApi.downloadBlobFileInline(guid);
  }

  /**
   * Retrieves a package product
   *
   * @param {IResPackageSelectParams} params
   * @return {*}  {Observable<ReservationBasePackage>}
   * @memberof ReservationApiService
   */
  public getPackage(params: IResPackageSelectParams): Observable<ReservationBasePackage> {
    return this.callDynamicAPI('product/selectPKG', params, 'POST')
      .pipe(
        map<DynamicApiResponse, ReservationBasePackage>((response) => new ReservationBasePackage(response.body))
      );
  }
  public getPackageAllocationStatus(params: IResParamsPackageAllocations): Observable<ResPackageAllocation> {
    return this.packageApi.getPackageAllocationStatus(params);
  }

  /**
   * Retrieves a package product Optimized
   *
   * @param {IResPackageSelectParams} params
   * @return {*}  {Observable<ReservationBasePackage>}
   * @memberof ReservationApiService
   */
  public getPackageOpt(params: IResPackageSelectParams): Observable<ReservationBasePackage> {
    return this.packageApi.getPackageOpt(params);
  }

  public getPackageVoyagePrices(voyageID?: number): Observable<ResCruisePriceType[]> {
    return this.inventoryApi.getPackageVoyagePrices(voyageID);
  }

  public getPackageOptionToSuiteCategoryList(packageID?: number | string): Observable<ResPackageOptionToSuiteCategory[]> {
    return this.inventoryApi.getPackageOptionToSuiteCategoryList(packageID);
  }

  public managePackageOptionToSuiteCategoryList(params: IResApiPackageOptionToSuiteCategory[]) {
    return this.inventoryApi.managePackageOptionToSuiteCategoryList(params);
  }


  /**
   * Retrieves a camp product
   *
   * @param {IResCampSelectParams} params
   * @return {*}  {Observable<ReservationCamp>}
   * @memberof ReservationApiService
   */
  public getCamp(params: IResCampSelectParams): Observable<ReservationCamp> {
    return this.productPriceApi.getCamp(params);
  }
  /**
   * Retrieves a service product
   *
   * @param {IResProductServiceSelectParams} params
   * @return {*}  {Observable<ResProductService>}
   * @memberof ReservationApiService
   */
  public getProductService(params: IResProductServiceSelectParams): Observable<ResProductService> {
    return this.productPriceApi.getProductService(params);
  }
  /**
   * Retrieves a service product
   *
   * @param {IResProductServiceSelectParams} params
   * @return {*}  {Observable<ReservationServiceType>}
   * @memberof ReservationApiService
   */
  getSearchableProductService(): Observable<ReservationServiceType[]> {
    return this.callDynamicAPI('search/allProductService', {}, 'GET').pipe(
      map<DynamicApiResponse, ReservationServiceType[]>((response) =>
        response.body.map((s: any) => new ReservationServiceType(s))
      )
    )
  }

  getSearchDurationFilter(companyID?: number): Observable<ResBkgDuration[]> {
    return this.productPriceApi.getSearchDurationFilter(companyID);
  }
  getDmcEDocUrl(bkgID: number, languageCode?: string, showFinancial: string = 'N', guestCopy: string = 'N'): Observable<string> {
    return this.bookingApi.getDmcEDocUrl(bkgID, languageCode, showFinancial, guestCopy);
  }
  /**
   *
   * Retrieves a package product
   *
   * @param {IResPackageSearchParams} params
   * @return {*}  {Observable<BookingPackage>}
   * @memberof ReservationApiService
   */
  public getPaginatedPackage(params: IResPackageSearchParams): Observable<DynamicApiResponsePaginatedModel<ResPackage>> {
    return this.productPriceApi.getPaginatedPackage(params);
  }

  public getPackageList(params: IResPackageSearchParams): Observable<ResPackage[]> {
    return this.coreApi.getPackageList(params);
  }

  public getPkgSearchCK(params: IResPackageSearchParams): Observable<ResPackage[]> {
    return this.packageApi.getPkgSearchCK(params);
  }


  /**
   *
   * Retrieves a package single supplement cost
   *
   * @param {IResPackageSearchParams} params
   * @return {*}  {Observable<BookingPackage>}
   * @memberof ReservationApiService
   */
  public getPackageSingleSupp(params: IResPackageSingleSuppParams): Observable<ResPackageSingleSupp[]> {
    return this.packageApi.getPackageSingleSupp(params);
  }
  getRightRezOfficeIdConfig(): Observable<ResRightRezOfficeIdConfig[]> {
    return this.bookingApi.getRightRezOfficeIdConfig();
  }

  public getShorexOptions(filter?: any): Observable<Array<ReservationShorexOption>> {
    return this.callDynamicAPI('shorex/selectOptions', filter, 'POST').pipe(
      map<DynamicApiResponse, Array<ReservationShorexOption>>((response) =>
        response.body.map((option: any) => new ReservationShorexOption(option))
      )
    );
  }

  public getPermission(): Observable<IReservationApiPermission> {
    return this.callDynamicAPI('reservation/permission', {}, 'POST').pipe(
      map<DynamicApiResponse, IReservationApiPermission>((response) => response.body)
    );
  }

  public getReservationDashboard() {
    const self = this;
    function createMenuCategory(item: any): IReservationHomeCategory {
      const menuCategory = {
        icon: {
          iconCode: item.ImageId,
          iconType: item.IconType || EidosIconType.SVG,
          iconSize: item.IconSize || 2
        } as IEidosIcon,
        title: item.Label,
        description: item.Description,
        children: [],
        isFavourites: false
      } as IReservationHomeCategory;
      return menuCategory;
    }

    function addToken(path: string) {
      const token = self.eidosSecurityService.getToken();
      if (!token) return path;
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}token=${token}`
    }

    function createMenuItem(item: any, items: Array<any>): IReservationHomeItem {
      const url = item.Module === '__integrated__' ? addToken(item.Path) : item.Path
      const menuItem: IReservationHomeItem = {
        id: item.MenuId,
        icon: {
          iconCode: item.IconId,
          iconType: item.IconType || EidosIconType.SVG,
          iconSize: item.IconSize || 1
        } as IEidosIcon,
        title: item.Label,
        description: item.Description,
        action: !!item.Path ? IReservationHomeItemActions.URL : undefined,
        actionData: {
          target: !!item.NewWindow ? '_blank' : '_self',
          url: url,
          safeUrl: self.domSanitizer.bypassSecurityTrustUrl(url),
          urlRelative: item.PathType ? item.PathType === 'R' : false,
          module: item.Module
        } as IReservationHomeActionData,
        children: [],
        isDefaultIsHighlight: item.IsDefaultIsHighlight ? item.IsDefaultIsHighlight === 'Y' : false,
        hasFavouriteStar: item.IsDefaultIsHighlight === 'N'
      };

      //add children
      items
        .filter((child) => child.ParentId === item.MenuId)
        .sort((a, b) => a.Pos - b.Pos)
        .forEach((child) => menuItem.children!.push(createMenuItem(child, items)));

      if (items.length > 0 && item.ImageId) {
        menuItem.icon.iconCode = item.ImageId;
        menuItem.icon.iconSize = item.IconSize || 2;
      }
      return menuItem;
    }
    function _prepareDashboard(items: Array<any>): Array<IReservationHomeCategory> {
      const homeMenu: Array<IReservationHomeCategory> = [];

      items
        .filter((item) => item.ParentId === 0)
        .sort((a, b) => a.Pos - b.Pos)
        .forEach((item) => {
          const menuItem = createMenuCategory(item);

          //add children
          items
            .filter((child) => child.ParentId === item.MenuId)
            .sort((a, b) => a.Pos - b.Pos)
            .forEach((child) => menuItem.children.push(createMenuItem(child, items)));

          homeMenu.push(menuItem);
        });

      return homeMenu;
    }
    return this.callDynamicAPI('oneis/home', {}, 'GET').pipe(
      map<DynamicApiResponse, Array<IReservationHomeCategory>>((response) =>
        response.body.HomeList ? _prepareDashboard(response.body.HomeList) : _prepareDashboard([])
      )
    );
  }


  /**
   * Search voyages with filters
   *
   * @param {IResApiVoyagesSearchParameters} [filters]
   * @return {DynamicApiResponsePaginatedModel<ReservationCruise>}
   * @memberof ReservationApiService
   */
  public getVoyagesSearch(filters: IResApiVoyagesSearchParameters) {
    return this.callDynamicAPI(
      'voyages/search',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationCruise>>(
        (response) => {
          const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationCruise>(response);
          paginatedModel.content = response.body.map(
            (cruise: any) => new ReservationCruise(cruise)
          );
          return paginatedModel;
        })
    );
  }
  public getPeopleSearch(filters: IResApiPeopleParameters) {
    return this.callDynamicAPI(
      'people/select',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ResPeople>>(
        (response) => {
          const paginatedModel = new DynamicApiResponsePaginatedModel<ResPeople>(response);
          paginatedModel.content = response.body.map(
            (people: any) => new ResPeople(people)
          );
          return paginatedModel;
        })
    );
  }

  peoplePackageManage(filters: IResApiPackagePeopleManageParameters) {
    return this.callDynamicAPI(
      'people/packageManage',
      filters,
      'POST'
    ).pipe(
      map<DynamicApiResponse, any>(response => response.body)
    );
  }
  peopleManage(filters: IResApiPeopleManageParameters) {
    return this.rescomApiService.peopleManage(filters);
  }

  getPeopleTypeList(): Observable<ResPeopleType[]> {
    return this.getDynamicAPI('people/getPeopleType').pipe(
      map<DynamicApiResponse, ResPeopleType[]>((response) => {
        return response.body.map((data: any) => new ResPeopleType(data))
      })
    );
  }

  getTourConsultantList(filters: IResApiPeopleParameters) {
    return this.rescomApiService.getTourConsultantList(filters);
  }

  getPromotionShips(promoID?: number) {
    return this.inventoryApi.getPromotionShips(promoID);
  }

  getPromoPackages(promoID?: number): Observable<ResPromoPackage[]> {
    return this.promoApi.getPromoPackages(promoID);
  }

  managePromoPackages(params?: IResApiManagePromoPackages) {
    return this.packageApi.managePromoPackages(params);
  }

  getPromotionPriceTypes(promoID?: number, companyID?: number | string) {
    return this.promoApi.getPromotionPriceTypes(promoID, companyID);
  }

  // packageRefreshPdf(packageID: number) {
  //   return this.callDynamicAPI(
  //     'package/PackagePdfRefresh',
  //     { PackageID: packageID },
  //     'GET'
  //   ).pipe(
  //     map<DynamicApiResponse, boolean>(
  //       (response) => response.errors.length == 0
  //     )
  //   );
  // }
  /**
   * Search voyages with filters
   *
   * @param {IResApiVoyagesSearchParameters} [filters]
   * @return {DynamicApiResponsePaginatedModel<ReservationCruise>}
   * @memberof ReservationApiService
   */
  public getAgenciesOrDirectsSearch(filters: IResApiAgenciesSearchParameters) {

    switch (filters.SearchType) {

      case ResApiAgenciesSearchType.Agencies:
        return this.callDynamicAPI(
          'agency/select',
          Object.assign({ Typology: 'AGN' }, filters),
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationAgency | ReservationIndividual>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationAgency>(response);
              paginatedModel.content = response.body.map(
                (agency: IReservationApiAgency) => new ReservationAgency(agency)
              );
              return paginatedModel;
            })
        );

      case ResApiAgenciesSearchType.Direct:
        return this.callDynamicAPI(
          'individual/select',
          filters,
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationIndividual>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationIndividual>(response);
              paginatedModel.content = response.body.map(
                (individual: IReservationApiIndividual) => new ReservationIndividual(individual)
              );
              return paginatedModel;
            })
        );

      case ResApiAgenciesSearchType.Consortium:
        return this.callDynamicAPI(
          'agency/select',
          Object.assign({ Typology: 'CNS' }, filters),
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationAgency>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationAgency>(response);
              paginatedModel.content = response.body.map(
                (agency: IReservationApiAgency) => new ReservationAgency(agency)
              );
              return paginatedModel;
            })
        );

      case ResApiAgenciesSearchType.HeartQuarter:
        return this.callDynamicAPI(
          'agency/select',
          Object.assign({ Typology: 'AHQ' }, filters),
          'GET'
        ).pipe(
          map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationAgency>>(
            (response) => {
              const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationAgency>(response);
              paginatedModel.content = response.body.map(
                (agency: IReservationApiAgency) => new ReservationAgency(agency)
              );
              return paginatedModel;
            })
        );
    }
  }

  getIndividualGuests(filters: IReservationApiIndividualsParameters) {
    return this.peopleApi.getIndividualGuests(filters);
  }

  checkReferralPastGuest(bkgID: number, params: { IndividualID: number }[]) {
    return this.peopleApi.checkReferralPastGuest(bkgID, params);
  }

  getReferralIndividualID(params: IResReferralParameters) {
    return this.peopleApi.getReferralIndividualID(params);
  }

  manageReferral(params: IResReferralParameters) {
    return this.peopleApi.manageReferral(params);
  }

  mailGuestChecker(individualID: number) {
    return this.peopleApi.mailGuestChecker(individualID);
  }

  getPortfolioGuests(individualID: number[], bkgID: number) {
    return this.peopleApi.getPortfolioGuests(individualID, bkgID);
  }

  managePortfolioDiscounts(bkgID: number, tblPortfolio: IReservationApiManageReferralDiscounts[]) {
    return this.peopleApi.managePortfolioDiscounts(bkgID, tblPortfolio);
  }

  checkReferralVerification(bkgID: number) {
    return this.peopleApi.checkReferralVerification(bkgID);
  }

  /**
   * Select inventory capacity of a cruise
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getInventorySelectCapacity(payload: IResVoyageSearchRequiredParams, owner: ResOwner) {
    if (owner.isValid) {
      if (owner.isAgent()) {
        payload.OwnerType = 'AGENCY'
        payload.AgencyCod = owner.agencyID;
      } else if (owner.isDirect()) {
        payload.OwnerType = "DIRECT";
      }
    }
    return this.callDynamicAPI('inventory/selectcapacity', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseInventoryModel>((response) => new ReservationCruiseInventoryModel(response.body))
    );
  }
  /**
   * Select inventory capacity of a cruise with multisuite
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  retriveInventorySelectCapacity(payload: IResVoyageSearchRequiredParams, owner: ResOwner) {
    return this.inventoryApi.retriveInventorySelectCapacity(payload, owner);
  }

  getInventorySuiteList(payload: IResVoyageSearchRequiredParams, amenities: IReservationSuiteAmenity[] = []) {
    return this.inventoryApi.getInventorySuiteList(payload, amenities);
  }

  getInventoryConnectedSuites(voyageID: number): Observable<ReservationCruiseSuiteListConnectedSuite[]> {
    return this.inventoryApi.getInventoryConnectedSuites(voyageID);
  }

  manageInventoryConnectedSuites(params: { connections: IReservationApiManageConnectedSuite[], voyageID: number }): Observable<any> {
    return this.inventoryApi.manageInventoryConnectedSuites(params);
  }


  getPackagePricesHistory(payload: IResPackagePricesHistoryParams) {
    return this.callDynamicAPI('package/pricesHistory', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationPackagePricesAndCostsHistory>((response) =>
        new ReservationPackagePricesAndCostsHistory(response.body)
      )
    );
  }


  /**
   * update inventory suites of a cruise
   *
   * @param {IResInventoryUpdateParams} payload
   * @return {*}
   * @memberof ReservationApiService
   */
  inventoryUpdate(payload: IResInventoryUpdateParams) {
    return this.inventoryApi.inventoryUpdate(payload);
  }

  gtyUpdate(payload: IResGtyUpdateParams) {
    return this.inventoryApi.gtyUpdate(payload);
  }
  /**
   * Retrieves cruise availability
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getCruiseAvailabilityNew(owner: ResOwner, payload: IReservationApiCruiseInventoryParams, showAllSuites: boolean = false) {
    return this.inventoryApi.getCruiseAvailabilityNew(owner, payload, showAllSuites);
  }
  getVoyageInventoryGTY(voyageID: number) {
    return this.inventoryApi.getVoyageInventoryGTY(voyageID);
  }
  getVoyageInventoryBySuite(voyageID: number) {
    return this.inventoryApi.getVoyageInventoryBySuite(voyageID);
  }
  getVoyageInventoryBySuiteBookings(voyageID: number) {
    return this.inventoryApi.getVoyageInventoryBySuiteBookings(voyageID);
  }

  getVoyageInventoryForInteractiveDeckplan(voyageID: number): Observable<ResInventoryByCategoryModel[]> {
    return this.inventoryApi.getVoyageInventoryForInteractiveDeckplan(voyageID);
  }
  /**
   * Get inventory setup "Availability" tab data
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getInventorySetupAvailability(voyageID: number, currency: string, priceTypeID: number) {
    return this.inventoryApi.getInventorySetupAvailability(voyageID, currency, priceTypeID);
  }
  /**
   * Get inventory setup "Inventory By Category" tab data
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getInventoryByCategory(voyageID: number) {
    return this.inventoryApi.getInventoryByCategory(voyageID);
  }
  /**
   * Get inventory setup "Price Suite By Voyage" tab data
   *
   * @return {*}
   * @memberof ReservationApiService
   */
  getPriceSuiteByVoyage(voyageID: number) {
    return this.inventoryApi.getPriceSuiteByVoyage(voyageID);
  }
  /**
   * Get current selected products
   *
   * @param {ResProductType} productCode
   * @param {number[]} [productIDs=[]]
   * @param {*} [filters]
   * @return {*}  {Promise<any[]>}
   * @memberof ResPreBookingUtility
   */
  getProductsPaymentScheduler(params: IResProductsPaymentSchedulerParams) {
    return this.productPriceApi.getProductsPaymentScheduler(params);
  }
  /**
   * Get suites deckplan of a cruise
   *
   * @param {IResSuiteDeckplanParams} payload
   * @return {*}
   * @memberof ReservationApiService
   */
  getSuiteDeckplan(payload: IResSuiteDeckplanParams) {
    return this.inventoryApi.getSuiteDeckplan(payload);
  }
  getDeckplanInfo(payload: IResDeckplanInfoParams) {
    return this.productPriceApi.getDeckplanInfo(payload);
  }
  //#region Booking

  /**
   * Create booking with specified params
   *
   * @param {IResApiParametersQuoteCreate} params
   * @return {*}  {Observable<IResBookingCreateApiResponse>}
   * @memberof ReservationApiService
   */
  createBooking(params: IResApiParametersQuoteCreate, multiBkg = false): Observable<IResBookingCreateApiResponse> {
    return this.bookingApi.createBooking(params, multiBkg);
  }

  getTailorMadeParametersNew(agencyCode?: number) {
    return this.productPriceApi.getTailorMadeParametersNew(agencyCode);
  }

  getTailorMadeParameters(agencyCode?: number) {
    return this.productPriceApi.getTailorMadeParameters(agencyCode);
  }
  /**
   * Retrieves booking items list
   *
   * @return {*}  {Observable<ResBkgItem[]>}
   * @memberof ReservationApiService
   */
  getBookingItems(bkgID: number, filters?: IResBkgItemApiParams): Observable<ResBkgGroupedItems> {

    return this.callDynamicAPI('booking/items', { BkgID: bkgID, ...filters }, 'POST').pipe(
      map<DynamicApiResponse, ResBkgGroupedItems>((response) => {

        const content = new ResBkgGroupedItems()

        const items = new DynamicApiResponsePaginatedModel<ResBkgItem>(response);

        items.content = response.body.items.map((item: any) => new ResBkgItem(item))
        const flitghtInfos: ResBkgItemFlightInfo[] = (response.body.flightInfo?.length ?? 0) > 0 ? response.body.flightInfo.map((fi: any) => new ResBkgItemFlightInfo(fi)) : []
        items.content
          .forEach(itm =>
            itm.supplements.forEach(s =>
              s.flitghtInfos = flitghtInfos.filter(f => f.itemID === s.itemID)
            )
          )
        content.itemsPaginated = items
        content.items = items.content
        content.total = new ResBkgItemTotal(response.body.total)



        let aggregationField = ''

        content.groups = response.body.groups.map((item: any, index: number) => {
          const g = new ResGroups()
          if (item.GuestCod) {
            aggregationField = 'guestCod'
            g.code = `${item.GuestCod}`
            g.description = item.GuestName
            g.id = item.GuestCod
          } else if (item.RoomConfHdrID) {
            aggregationField = 'roomConfHdrID'
            g.code = `${item.RoomConfHdrID}`
            g.description = `G${index + 1}`
            g.id = item.RoomConfHdrID
          } else if (item.ProductCod) {
            aggregationField = 'productCod'
            g.code = item.ProductCod
            g.description = item.ProductName
            g.id = index + 1
            g.attributes = [
              { productName: item.ProductName },
              { itemName: item.ItemName },
              { suiteCategoryID: item.SuiteCategoryID },
              { suiteCategory: item.SuiteCategory },
              { priceCategoryID: item.PriceCategoryID },
              { priceCategory: item.PriceCategory },
              { suiteNo: item.SuiteNo },
            ]
          } else if (item.SupplierID) {
            aggregationField = 'supplierID'
            g.code = `${item.SupplierID}`
            g.description = item.SupplierName
            g.id = item.SupplierID
          }

          return g
        })

        let row = 0, lastCodes = ''
        items.content.forEach(item => {
          item.aggregationDescription = ''
          item.aggregationField = aggregationField
          const codes = `${(item as any)[aggregationField]}`;

          if (lastCodes !== codes) {
            row = 0
            lastCodes = codes
          }
          item.aggregationGroupRow = ++row

          codes.split(',').forEach(code => {
            const group = content.groups.find(g => g.code == code)
            if (group && !item.aggregations.find(ag => ag.code === code)) {
              item.aggregations.push(group)
              item.aggregationDescription += ', ' + group.description
            }
          })

          item.aggregationCodes = codes
          item.aggregationDescription = item.aggregationDescription.replace(/^\, /, '')
        })

        return content
      }),
      catchError((_response: IDynamicApiResponse) => {
        return [];
      })
    );
  }

  manageMultiItems(bkgID: number, items: IResApiBkgItemsManage[], source?: string, tailorShift?: string) {
    return this.bookingApi.manageMultiItems(bkgID, items, source, tailorShift);
  }
  setMarginOverlayItems(bkgID: number, filters?: IResBkgItemApiParams) {
    return this.callDynamicAPI('margin/overlaySet', { BkgID: bkgID, ...filters }, 'POST');
  }
  getMarginOverlayItems(bkgID: number, filters?: IResBkgItemApiParams): Observable<ResBkgGroupedItems> {
    if(filters) filters.IsMarginOverlayTab = 'Y'

    return this.callDynamicAPI('margin/overlayGet', { BkgID: bkgID, ...filters }, 'POST').pipe(
      map<DynamicApiResponse, ResBkgGroupedItems>((response) => {

        const content = new ResBkgGroupedItems()

        const items = new DynamicApiResponsePaginatedModel<ResBkgItem>(response);

        const groups: ResGroups[] = response.body.DT0 ?? []
        const dataItems: ResBkgItem[] = response.body.DT1 ?? []
        const total: ResBkgItemTotal[] = response.body.DT2 ?? []
        const flightInfo: ResBkgItemFlightInfo[] = []
        const overlay: ResBkgItemOverlay[] = response.body.DT3 ?? []
        const overlayNote: string = response.body.DT4 && response.body.DT4?.length > 0 ? response.body.DT4[0].BookingNote : ''
        const overlayPerPax: ResBkgItemOverlayPerPax[] = response.body.DT5 ?? []
        const overlayPerGroup: ResBkgItemOverlayPerGroup[] = response.body.DT6 ?? []
        const overlayMarginCountries: ResBkgItemOverlayMarginCountry[] = response.body.DT7 ?? []

        items.content = dataItems.map((item: any) => new ResBkgItem(item))
        const flitghtInfos: ResBkgItemFlightInfo[] = flightInfo.map((fi: any) => new ResBkgItemFlightInfo(fi))
        items.content
          .forEach(itm =>
            itm.supplements.forEach(s =>
              s.flitghtInfos = flitghtInfos.filter(f => f.itemID === s.itemID)
            )
          )
        content.itemsPaginated = items
        content.items = items.content
        content.total = new ResBkgItemTotal(total[0])
        content.allTotals = total.map(item=>new ResBkgItemTotal(item))
        content.overlay = overlay.map((item: any) => new ResBkgItemOverlay(item))
        content.overlayPerPax = overlayPerPax.map((item: any) => new ResBkgItemOverlayPerPax(item))
        content.overlayPerGroup = overlayPerGroup.map((item: any) => new ResBkgItemOverlayPerGroup(item))
        content.overlayNote = overlayNote
        content.overlayMarginCountries = overlayMarginCountries.map((item: any) => new ResBkgItemOverlayMarginCountry(item))

        let aggregationField = ''

        content.groups = groups.map((item: any, index: number) => {
          const g = new ResGroups()
          g.country = item.Country ?? ''
          if (item.GuestCod) {
            aggregationField = 'guestCod'
            g.code = `${item.GuestCod}`
            g.description = item.GuestName
            g.id = item.GuestCod
          } else if (item.RoomConfHdrID) {
            aggregationField = 'roomConfHdrID'
            g.code = `${item.RoomConfHdrID}`
            g.description = `G${index + 1}`
            g.id = item.RoomConfHdrID
          } else if (item.ProductCod) {
            aggregationField = 'productCod'
            g.code = item.ProductCod
            g.description = item.ItemName
              ? `${item.ProductName} - ${item.ItemName}`
              : item.ProductName
            g.id = index + 1
            g.attributes = [
              { productName: item.ProductName },
              { itemName: item.ItemName },
              { suiteCategoryID: item.SuiteCategoryID },
              { suiteCategory: item.SuiteCategory },
              { priceCategoryID: item.PriceCategoryID },
              { priceCategory: item.PriceCategory },
              { suiteNo: item.SuiteNo },
            ]
          } else if (item.SupplierID) {
            aggregationField = 'supplierID'
            g.code = `${item.SupplierID}`
            g.description = item.SupplierName
            g.id = item.SupplierID
          }

          return g
        })

        let row = 0, lastCodes = ''
        items.content.forEach(item => {
          item.aggregationDescription = ''
          item.aggregationField = aggregationField
          const codes = item.productCod==='MRO' ? 'MRO' : `${(item as any)[aggregationField]}`;

          if (lastCodes !== codes) {
            row = 0
            lastCodes = codes
          }
          item.aggregationGroupRow = ++row

          codes.split(',').forEach(code => {
            const group = content.groups.find(g => g.code == code)
            if (group && !item.aggregations.find(ag => ag.code === code)) {
              if (!group.country && item.country) {
                group.country = item.country
              }
              item.aggregations.push(group)
              item.aggregationDescription += ', ' + group.description
            }
          })

          item.aggregationCodes = codes
          item.aggregationDescription = item.aggregationDescription.replace(/^\, /, '')
        })

        content.items
              .filter(item=>item.aggregationGroupRow===1)
              .forEach(group=>{
                const row = content.items
                                    .filter(item=>item.aggregationCodes===group.aggregationCodes)
                row.forEach(item=>{
                  const c = item.costCurrency
                  let idx = group.aggregationsCostTotals.findIndex(t=>t.currency===c)
                  if(idx===-1) idx = group.aggregationsCostTotals.push({amount:0,currency:c}) - 1
                  const costTotal = group.aggregationsCostTotals[idx].amount + item.cost
                  group.aggregationsCostTotals[idx].amount = costTotal

                  idx = group.aggregationsCostTaxTotals.findIndex(t=>t.currency===c)
                  if(idx===-1) idx = group.aggregationsCostTaxTotals.push({amount:0,currency:c}) - 1
                  const costTaxTotal = group.aggregationsCostTaxTotals[idx].amount + item.costTax
                  group.aggregationsCostTaxTotals[idx].amount = costTaxTotal

                  idx = group.aggregationsCostGrossTotals.findIndex(t=>t.currency===c)
                  if(idx===-1) idx = group.aggregationsCostGrossTotals.push({amount:0,currency:c}) - 1
                  const costGrossTotal = (group.aggregationsCostGrossTotals[idx].amount ?? 0) + item.grossCost
                  group.aggregationsCostGrossTotals[idx].amount = costGrossTotal
                })
              })
        return content
      }),
      catchError((response: IDynamicApiResponse) => {
        throw new DynamicApiError(response)
      })
    );
  }

  /**
   * Retrieves booking cancel reasons list
   *
   * @return {*}  {Observable<ResBookingCancelReason>}
   * @memberof ReservationApiService
   */
  getBookingCancelReason(bkgID?: number): Observable<Array<ResBookingCancelReason>> {
    return this.bookingApi.getBookingCancelReason(bkgID);
  }
  getReinstateItemList(bkgID: number): Observable<BookingJourneySupplement[]> {
    return this.bookingApi.getReinstateItemList(bkgID);
  }
  saveRoomConfig(bkgID: number, rooms: IResApiSaveRoomConfigRoom[]): Observable<DynamicApiResponse> {
    return this.bookingApi.saveRoomConfig(bkgID, rooms);
  }
  manageReinstateItemList(bkgID: number): Observable<DynamicApiResponse> {
    return this.bookingApi.manageReinstateItemList(bkgID);
  }
  getFccCertificate(bkgID?: number): Observable<ResFccCalculation[]> {
    return this.bookingApi.getFccCertificate(bkgID);
  }
  public normalizeBookingGet(data: any) {
    return this.coreApi.normalizeBookingGet(data);
  }




  /**
   * Retrieves booking matching with filters specified
   *
   * @param {IReservationApiBookingRequestParameters} [filter]
   * @param {boolean} [lightMode=false] Specify to retrieve booking in a minimal structure
   * @return {*}  {Observable<ResBooking>}
   * @memberof ReservationApiService
   */
  getBooking(
    filter?: IReservationApiBookingRequestParameters,
    lightMode: boolean = false
  ): Observable<ResBooking> {
    return this.coreApi.getBooking(filter, lightMode);
  }
  /**
   * Post booking edit params to edit a booking
   *
   * @param {IReservationApiEditBookingParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  editBooking(params: IReservationApiEditBookingParameters) {
    return this.bookingApi.editBooking(params);
  }
  /**
   * Post booking version request params to recalculate a booking
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  recalculateBooking(params: IReservationApiBookingRequestParameters) {
    return this.bookingApi.recalculateBooking(params);
  }
  /**
   * Performs booking version confirmation
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  confirmBooking(params: IReservationApiBookingVersionRequestParameters) {
    return this.bookingApi.confirmBooking(params);
  }
  /**
   * Perform booking version deletion
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  deleteBooking(params: IReservationApiBookingDelete) {
    return this.bookingApi.deleteBooking(params);
  }
  /**
   * Perform booking version cloning
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  cloneBooking(params: IReservationApiBookingVersionRequestParameters) {
    return this.bookingApi.cloneBooking(params);
  }
  /**
   * Request autheticated url to open myactivities on web site
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  getMyActivitiesUrl(params: IReservationApiBookingGetMyActivitiesUrlParameters) {
    return this.getExternalAPI('oem/crystalws/Agent/OnBehalfOf', 'POST', params).pipe(
      map<any, string>(res => {
        const url = res.Body.url ?? '';
        return url;
      })
    );
  }
  getExternalGifUrl(params: IReservationApiBookingExternalGifUrlParameters) {
    return this.bookingApi.getExternalGifUrl(params);
  }
  getSpResult(spCode: string) {
    return this.getExternalAPI('oem/dd/listdata', 'POST', { name: spCode }).pipe(
      map<any, any[]>(res => {
        if (res.HasError) return of([])
        return res.listdata;
      })
    );
  }


  getDynamicMap(prms: IDynamicMapApiParams) {
    return this.packageApi.getDynamicMap(prms);
  }

  /**
   * Request upsert guest on Travel studio
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  upsertGuestOnTS(params: IReservationApiTsUpserGuest) {
    return this.bookingApi.upsertGuestOnTS(params);
  }
  /**
   * Request autheticated url to open myactivities on web site
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  bookingAddPackage(params: IReservationApiBookingAddPackageParameters) {
    return this.bookingApi.bookingAddPackage(params);
  }
  /**
   * Check if can perform booking action
   *
   * @param {IReservationApiBookingActionCheckParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  checkBookingAction(params: IReservationApiBookingActionCheckParameters) {
    return this.bookingApi.checkBookingAction(params);
  }
  /**
   * Performs a booking action
   *
   * @param {IReservationApiBookingActionParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  executeBookingAction(params: IReservationApiBookingActionParameters, options?: any) {
    return this.bookingApi.executeBookingAction(params, options);
  }
  /**
   * Retrieves booking guests
   *
   * @param {number} bkgID
   * @param {number} sectionID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingGuests(bkgID: number, sectionID: number) {
    return this.packageApi.getBookingGuests(bkgID, sectionID);
  }
  /**
   * Retrieves booking itinerary
   *
   * @param {number} bkgID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingItinerary(bkgID: number) {
    return this.packageApi.getBookingItinerary(bkgID);
  }
  /**
   * Retrieves booking financials
   *
   * @param {number} bkgID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingFinancials(bkgID: number) {
    return this.bookingApi.getBookingFinancials(bkgID);
  }

  public getDebugBkgItinerary(bkgID: number): Observable<ResDebugBkgItinerary> {
    return this.bookingApi.getDebugBkgItinerary(bkgID);
  }

  public getDebugPkgItinerary(packageID: number): Observable<ResDebugBkgItinerary> {
    return this.bookingApi.getDebugPkgItinerary(packageID);
  }
  /**
   * Convert an OD to a booking
   *
   * @param {ResDepositToBookingConversionParams} params
   * @return {*}
   * @memberof ReservationApiService
   */
  convertODToBkg(params: ResDepositToBookingConversionParams) {
    return this.bookingApi.convertODToBkg(params);
  }
  /**
   * Perform booking V2V
   *
   * @param {ResBookingV2VParams} params
   * @return {*}
   * @memberof ReservationApiService
   */
  bookingV2V(params: ResBookingV2VParams) {
    return this.bookingApi.bookingV2V(params);
  }
  /**
   * Get a booking guest booked activities
   *
   * @param {IResGetGuestBookedActivitiesParams} params
   * @return {Observable<ReservationBookingActivitiesBooked>}
   * @memberof ReservationApiService
   */
  getGuestBookedActivities(params: IResGetGuestBookedActivitiesParams): Observable<ReservationBookingActivitiesBooked> {
    return this.getDynamicAPI('booking/guestBookedActivities', params).pipe(
      map<DynamicApiResponse, ReservationBookingActivitiesBooked>((response) => {
        return new ReservationBookingActivitiesBooked(response.body);
      })
    );
  }

  getGuestTypesForBkg(params: { BkgID: number }): Observable<IReservationLoyaltyDiscount[]> {
    return this.bookingApi.getGuestTypesForBkg(params);
  }

  getAllLinkedBkgs(params: { BkgID: number }): Observable<ResBkgLinks> {
    return this.bookingApi.getAllLinkedBkgs(params);
  }

  getBkgSuggestedItems(params: IResApiGetBkgSuggestedItems): Observable<ResBkgSuggestedItem[]> {
    return this.bookingApi.getBkgSuggestedItems(params);
  }

  getBkgExtraItems(params: { bkgID: number, serviceID: number, itineraryDate: DateTime }): Observable<ResBkgExtraItem[]> {
    return this.bookingApi.getBkgExtraItems(params);
  }
  /**
   * Get links to other bkgs of current bkg
   *
   * @param {({ BkgID: number } | { Sequence: number })} params
   * @return {*}  {Observable<IReservationLinkedBooking[]>}
   * @memberof ReservationApiService
   */
  getLinkedBkgsOfBkg(params: { BkgID: number } | { Sequence: number }): Observable<ResBkgLinked[]> {
    return this.callDynamicAPI('booking/getLinks', params, 'GET').pipe(
      map<DynamicApiResponse, ResBkgLinked[]>((response) => response.body.map((i: any) => {
        const item = new ResBkgLinked(i)
        item.status = 'A'
        return item
      }))
    );
  }
  /**
   * Checks is if can be linked to bkgidprv as IsSameVyg
   *
   * @param {{ BkgID: number, BkgIDPrv: number, IsSameVyg: ReservationApiBoolean }} params
   * @return {*}  {(Observable<(any[] | {})>)}
   * @memberof ReservationApiService
   */
  checkBkgLink(params: { BkgID: number, BkgIDPrv: number, IsSameVyg: ReservationApiBoolean, UniqueExperience?: string }): Observable<(any[] | {})> {
    return this.bookingApi.checkBkgLink(params);
  }

  manageBkgLinks(params: IResApiLinkedBookingManageParams): Observable<(any[] | {})> {
    return this.bookingApi.manageBkgLinks(params);
  }

  /**
   * Save OD notes
   *
   * @param {number} bkgID
   * @param {string} newNote
   * @return {*}
   * @return {*}
   * @memberof ReservationApiService
   */
  saveBookingNotes(bkgID: number, note: string) {
    return this.bookingApi.saveBookingNotes(bkgID, note);
  }

  //#endregion Booking

  /**
   * Retrieves cruise availability
   *
   * @return {*}  {Observable<ReservationAvailabilityModel>}
   * @memberof ReservationApiService
   */
  public getCruiseAvailabilityWithDetails(voyageId: number, route: string = 'cruise/availability') {
    return this.callDynamicAPI(route, { voyageId }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseAvailabilityModel>((response) => {
        const reservationCruiseAvailability = new ReservationCruiseAvailabilityModel(response.body);
        return reservationCruiseAvailability;
      })
    );
  }

  /**
   *  Get Individuals, used in bookin guests search
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getIndividuals(filters?: IReservationApiIndividualsParameters) {
    return this.callDynamicAPI('individual/select', filters, 'GET').pipe(
      map<DynamicApiResponse, ReservationIndividual[]>((response) => {
        return response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r));
      })
    );
  }

  public getPaginatedIndividuals(filters?: IReservationApiIndividualsParameters, loadOptions?: any) {
    return this.peopleApi.getPaginatedIndividuals(filters, loadOptions);
  }



  /**
   * Get Owners, used in owners search
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @param {*} [loadOptions]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getOwners(filters?: IReservationApiIndividualsParameters) {
    return this.callDynamicAPI('individualOwners/select', filters, 'GET').pipe(
      map<DynamicApiResponse, ReservationIndividual[]>((response) => {
        return response.body.map((r: IReservationApiIndividual) => new ReservationIndividual(r));
      })
    );
  }

  public getPaginatedOwners(filters?: IReservationApiIndividualsParameters, loadOptions?: any) {
    return this.peopleApi.getPaginatedOwners(filters, loadOptions);
  }

  /**
   * Get individual household
   *
   * @param {IReservationApiIndividualsParameters} [filters]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getHousehold(household_id: number) {
    return this.peopleApi.getHousehold(household_id);
  }


  public guestCheckBookingExists(params: IResApiGuestCheckBookingExistsParams): Observable<{ current: IResApiGuestCheckBookingExistsResult[], next: IResApiGuestCheckBookingExistsResult[], previous: IResApiGuestCheckBookingExistsResult[] }> {
    return this.bookingApi.guestCheckBookingExists(params);
  }

  /**
   * Manage Individuals
   *
   * @param {IReservationApiIndividual} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public manageIndividuals(params: IReservationApiIndividual) {
    return this.peopleApi.manageIndividuals(params);
  }

  /**
   * Get Agencies
   *
   * @param {IReservationApiAgency} [filters]
   * @return {*}
   * @memberof ReservationApiService
   */
  public getAgencies(filters?: IReservationApiAgency) {
    return this.packageApi.getAgencies(filters);
  }

  public getPaginatedAgencies(filters?: IReservationApiAgency, loadOptions?: any) {
    return this.peopleApi.getPaginatedAgencies(filters, loadOptions);
  }

  /**
   * Get Agents by Agency
   *
   * @param {IReservationApiAgentParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public getAgents(params: IReservationApiAgentParameters) {
    return this.callDynamicAPI('agent/agency', params, 'GET').pipe(
      map<DynamicApiResponse, ReservationAgent[]>((response) => {
        return response.body.map((r: IReservationApiAgent) => new ReservationAgent(r));
      })
    );
  }

  public getPaginatedAgents(filters: IReservationApiAgentParameters, loadOptions?: any) {
    return this.peopleApi.getPaginatedAgents(filters, loadOptions);
  }

  /**
   * Manage Agencies
   *
   * @param {IReservationApiAgency} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public manageAgencies(params: IReservationApiAgency) {
    return this.peopleApi.manageAgencies(params);
  }

  /**
   * Manage Agents
   *
   * @param {IReservationApiAgent} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public manageAgents(params: IReservationApiAgent) {
    return this.peopleApi.manageAgents(params);
  }

  /**
   * Create Guest
   *
   * @param {IReservationApiInsertGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public insertGuest(params: IReservationApiInsertGuestParameters) {
    return this.peopleApi.insertGuest(params);
  }

  /**
   * Update Guest
   *
   * @param {IReservationApiUpdateGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public updateGuest(params: IReservationApiUpdateGuestParameters) {
    return this.peopleApi.updateGuest(params);
  }

  /**
   * Delete Guest
   *
   * @param {IReservationApiDeleteGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public deleteGuest(params: IReservationApiDeleteGuestParameters) {
    return this.peopleApi.deleteGuest(params);
  }

  /**
   * Associate Directs to Guest
   *
   * @param {IReservationApiReplaceGuestParameters} params
   * @return {*}
   * @memberof ReservationApiService
   */
  public replaceGuest(params: IReservationApiReplaceGuestParameters) {
    return this.peopleApi.replaceGuest(params);
  }

  public getCountries(search?: string, cod?: number): Observable<Array<any>> {
    return this.coreApi.getCountries(search, cod);
  }

  public getStates(params: IReservationApiGetState): Observable<Array<any>> {
    return this.coreApi.getStates(params);
  }

  public getCounties(params: IReservationApiGetCounty): Observable<Array<any>> {
    return this.coreApi.getCounties(params);
  }

  public getOccupations(Occupation?: string): Observable<Array<any>> {
    return this.callDynamicAPI('occupation/select', { Occupation }).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getCashAccounts(params: { CurrencyCod: string, ReceiptTypeID: number, DebitCredit?: string, BkgID?: number }): Observable<Array<ResCashReceiptCashAccount>> {
    return this.cashApi.getCashAccounts(params);
  }

  public getCheckNo(): Observable<Array<IReservationCashReceiptCheckNO>> {
    return this.cashApi.getCheckNo();
  }

  getPaymentRefundInfo(bkgID: number,orderID?: string): Observable<ResPaymentRefundInfo> {
    return this.cashApi.getPaymentRefundInfo(bkgID, orderID);
  }

  public getPaymentReasons(): Observable<Array<IReservationCashReceiptPaymentReason>> {
    return this.callDynamicAPI('paymentReasons/select').pipe(
      map<DynamicApiResponse, Array<IReservationCashReceiptPaymentReason>>((response) => {
        return response.body;
      })
    );
  }
  public getPayerGroup(bkgId: number): Observable<ReservationCashReceiptPayerGroup[]> {
    return this.cashApi.getPayerGroup(bkgId);
  }
  /**
   * Retrieves all possible booking groups
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<ResGuestGroup>>}
   * @memberof ReservationApiService
   */
  public getBookingGuestGroups(bkgID: number): Observable<ResGuestGroup[]> {
    return this.callDynamicAPI('booking/guestgroups', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResGuestGroup[]>((response) => {
        return response.body.map((data: any) => new ResGuestGroup(data))
      })
    );
  }
  /**
   * Retrieves all possible booking payers
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<ResPayerGroup>>}
   * @memberof ReservationApiService
   */
  public getBookingPayers(bkgID: number): Observable<ResPayerGroup[]> {
    return this.callDynamicAPI('booking/payers', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResPayerGroup[]>((response) => {
        return response.body.map((data: any) => new ResPayerGroup(data))
      })
    );
  }

  public getBookingPayersList(bkgID: number): Observable<ResPayerGroupList[]> {
    return this.callDynamicAPI('booking/getPayersNew', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResPayerGroupList[]>((response) => {
        return response.body.map((data: any) => new ResPayerGroupList(data))
      })
    );
  }
  /**
   * Retrieves all possible booking payers
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<ResPayerGroup>>}
   * @memberof ReservationApiService
   */
  public getPayerDepartments(bkgID: number): Observable<ResPayerDepartment[]> {
    return this.callDynamicAPI('payer/departments', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResPayerDepartment[]>((response) => {
        return response.body.map((data: any) => new ResPayerDepartment(data))
      })
    );
  }
  manageBookingGuestGroups(bkgID: number, guestGroups: ResGuestGroup[]) {
    return this.postDynamicAPI('booking/managegroups', { BkgID: bkgID, GroupGuest: guestGroups }).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        return response.errors.length === 0
      })
    );
  }
  /**
   * Retrieves all booking possible groups
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<IResBookingGroup>>}
   * @memberof ReservationApiService
   */
  public getBookingGroups(params: { BkgID: number, GrpID?: number }): Observable<Array<IResBookingGroup>> {
    return this.bookingApi.getBookingGroups(params);
  }
  public getDMCReference(bkgID: number): Observable<IReservationApiDMCReference[]> {
    return this.bookingApi.getDMCReference(bkgID);
  }
  public getCompanyAgents(companyID: number): Observable<{ CompanyID: number; UserID: number; Login: string; Display_name: string }[]> {
    return this.bookingApi.getCompanyAgents(companyID);
  }
  public manageDMCReference(params: IReservationApiManageDMCReferenceParams): Observable<any> {
    return this.bookingApi.manageDMCReference(params);
  }

  manageEditAgeGuest(params: { BkgID: number; GuestID: number; Age: number }): Observable<any> {
    return this.bookingApi.manageEditAgeGuest(params);
  }
  /**
   * Retrieves all booking agent owners
   *
   * @return {*}  {Observable<Array<any>>}
   * @memberof ReservationApiService
   */
  public getBookingAgentOwners(bkgID: number, search?: string, id?: string): Observable<Array<any>> {
    return this.bookingApi.getBookingAgentOwners(bkgID, search, id);
  }
  /**
   * Retrieves booking custom request
   *
   * @param {number} bkgID
   * @param {number} sectionID
   * @return {*}
   * @memberof ReservationApiService
   */
  public getBookingCustomRequest(bkgID: number, sectionID: number) {
    return this.packageApi.getBookingCustomRequest(bkgID, sectionID);
  }

  public saveBookingCustomRequest(req: IReservationApiSaveCustomRequest) {
    return this.packageApi.saveBookingCustomRequest(req);
  }

  public getDeposit(params: any): Observable<any> {
    return this.cashApi.getDeposit(params);
  }

  public getBkgServices(params: IReservationGetBkgServicesParams, loadOptions?: any): Observable<any> {
    return this.bookingApi.getBkgServices(params, loadOptions);
  }
  bkgItemChangePriceType(params:IResBkgItemChangePriceType): Observable<any> {
    return this.bookingApi.bkgItemChangePriceType(params);
  }
  public getBkgOptionPrices(params: IReservationGetBkgOptionsParams, roomConfig: IServiceOptionGuest2Room[]): Observable<{data:ReservationServiceOption[],totalCount:number}> {
    return this.bookingApi.getBkgOptionPrices(params, roomConfig);
  }


  public getBkgOptions(params: IReservationGetBkgOptionsParams, loadOptions?: any): Observable<{data:ReservationServiceOption[],totalCount:number}> {
    return this.bookingApi.getBkgOptions(params, loadOptions);
  }

  addGuestToItem(params:IReservationAddGuestToItemParams) {
    return this.bookingApi.addGuestToItem(params);
  }
  public addServiceOptions(params: IReservationAddServiceOptionParams) {
    return this.coreApi.addServiceOptions(params);
  }
  public addExtraOptions(params: IReservationAddExtraOptionParams) {
    return this.bookingApi.addExtraOptions(params);
  }

  public replaceServiceOptions(params: IResApiReplaceOptionsParams): Observable<IResApiReplaceStatus[]> {
    return this.manifestApi.replaceServiceOptions(params);
  }

  public checkReplaceServiceOptionsAvl(params: IResApiReplaceOptionsParams): Observable<IResApiReplaceAvailability[]> {
    return this.manifestApi.checkReplaceServiceOptionsAvl(params);
  }

  public getReplaceServiceOptionsHistory(params: IResApiGetReplaceOptionsHistoryParams, loadOptions: any): Observable<{ data: ResReplaceHistory[], totalCount: number }> {
    return this.manifestApi.getReplaceServiceOptionsHistory(params, loadOptions);
  }

  public editServiceOption(params: IReservationEditServiceOptionParams) {
    return this.bookingApi.editServiceOption(params);
  }

  public deleteOptionPackage(params: IReservationDeleteOptionPackageParams) {
    return this.bookingApi.deleteOptionPackage(params);
  }

  public manageFlagsSupplements(params: IResManageMultipleEditItem) {
    return this.bookingApi.manageFlagsSupplements(params);
  }



  public execMarginCalculator(bkgID: number) {
    return this.bookingApi.execMarginCalculator(bkgID);
  }

  public getMarginCalculatorDetail(params: { MarginCalculatorParameterID: number, ItemID: number }) {
    return this.serviceSetupApi.getMarginCalculatorDetail(params);
  }

  public getDmcCommission(bkgID: number): Observable<ResDmcCommission[]> {
    return this.commissionApi.getDmcCommission(bkgID);
  }

  public getDmcCommissionItems(bkgID: number): Observable<IResDmcCommissionItemsResult> {
    return this.commissionApi.getDmcCommissionItems(bkgID);
  }

  public getDmcCommissionPayout(bkgID: number): Observable<IResDmcCommissionPayoutResult> {
    return this.callDynamicAPI('booking/getDMCCommissionPayout', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, IResDmcCommissionPayoutResult>((response) => {
        const body = response.body;
        return {
          agency: Array.isArray(body?.DT0) && body.DT0.length ? new ResDmcCommissionPayoutAgency(body.DT0[0]) : undefined,
          receipts: Array.isArray(body?.DT1) ? body.DT1.map((row: any) => new ResDmcCommissionPayoutReceipt(row)) : [],
        };
      })
    );
  }

  public manageDmcCommissionPayout(params: IResApiManageDmcCommissionPayoutParams): Observable<any> {
    return this.postDynamicAPI('booking/manageDMCCommissionPayout', params);
  }

  public saveDmcCommission(
    bkgID: number,
    dmcCommissionItemList: IResApiDmcCommissionItemListRow[],
    dmcCommissionTypeChoice: IResApiDmcCommissionTypeChoiceRow[]
  ): Observable<any> {
    return this.commissionApi.saveDmcCommission(bkgID, dmcCommissionItemList, dmcCommissionTypeChoice);
  }

  public getCommission(bkgID: number): Observable<any> {
    return this.commissionApi.getCommission(bkgID);
  }

  public updateCommissionAmount(params: IResApiUpdateCommissionAmount) {
    return this.commissionApi.updateCommissionAmount(params);
  }

  public getRemittance(params: IResApiGetRemittanceParams): Observable<IResApiGetRemittanceResponse | undefined> {
    return this.commissionApi.getRemittance(params);
  }

  public generateRemittance(params: IResApiGenerateRemittanceParams): Observable<any> {
    return this.commissionApi.generateRemittance(params);
  }

  /**
   * Does a transfer cash between bookings or groups
   *
   * @param {IResTransferCashParams} params
   * @return {Observable<{result: boolean, message: string}>}
   * @memberof ReservationApiService
   */
  public transferCash(params: IResTransferCashParams[]): Observable<{ result: boolean; message: string }> {
    return this.cashApi.transferCash(params);
  }

  public createCashReceipt(params: IReservationApiCreateCashReceiptParams): Observable<any> {
    return this.cashApi.createCashReceipt(params);
  }

  public checkComboAvailability(params: IReservationCheckComboAvailabilityParams) {
    return this.bookingApi.checkComboAvailability(params);
  }

  public updateMessage(params: IReservationUpdateMessageParams) {
    return this.bookingApi.updateMessage(params);
  }

  public createInvoiceForBkg(params: IReservationApiCreateBkgDocumentParams): Observable<any> {
    return this.bookingApi.createInvoiceForBkg(params);
  }

  public getInvoiceTypes(BkgID?: number) {
    return this.bookingApi.getInvoiceTypes(BkgID);
  }

  public getInvoiceSendingTypes() {
    return this.bookingApi.getInvoiceSendingTypes();
  }

  public deleteVoyage(params: IReservationApiDeleteCruiseParams): Observable<any> {
    return this.bookingApi.deleteVoyage(params);
  }

  public linkedPackageUpdate(params: IReservationApiLikedPackageUpdateParams): Observable<any> {
    return this.postDynamicAPI('package/linkedPackage', params);
  }

  public deletePackage(params: IReservationApiDeletePackageParams): Observable<any> {
    return this.bookingApi.deletePackage(params);
  }

  public cloneServices(params?: IReservationApiCloneServices): Observable<ReservationServiceData> {
    return this.serviceSetupApi.cloneServices(params);
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }




  public getServicesOptionsIncludedDetail(serviceID: number, optionID: number): Observable<ReservationSetupServiceOptionInclusiveDetail[]> {
    return this.serviceSetupApi.getServicesOptionsIncludedDetail(serviceID, optionID);
  }

  public getServicesOptions(params?: IReservationApiGetOptions): Observable<ReservationServiceOption[]> {
    return this.packageApi.getServicesOptions(params);
  }

  public getSetupOptionsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupOption>> {
    return this.serviceSetupApi.getSetupOptionsPaginated(params, loadOptions);
  }

  public getSetupSequenceDiscountsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupOption>> {
    return this.serviceSetupApi.getSetupSequenceDiscountsPaginated(params, loadOptions);
  }


  public getSequentialDiscountCods(companyId: number): Observable<string[]> {
    return this.serviceSetupApi.getSequentialDiscountCods(companyId);
  }



  public getSetupOptions(params?: IReservationApiGetOptions): Observable<ReservationSetupOption[]> {
    return this.serviceSetupApi.getSetupOptions(params);
  }

  public getOptions(params?: IReservationApiGetOptions): Observable<ReservationServiceOption[]> {
    return this.packageApi.getOptions(params);
  }

  public getPkgEResAllocation(params: IResApiPkgEResAllocation): Observable<ResPkgEResAllocation[]> {
    return this.packageApi.getPkgEResAllocation(params);
  }
  public managePkgEResAllocation(params: IResApiPkgEResAllocationManageParams): Observable<any> {
    return this.packageApi.managePkgEResAllocation(params);
  }

  public manageOptionAvailabilityAllPt(params: IReservationApiManageOptionAvailability): Observable<any> {
    return this.postDynamicAPI('optionAvailability/manageAllPt', params);
  }

  public manageOption(params?: any): Observable<any> {
    return this.serviceSetupApi.manageOption(params);
  }

  public manageSequentialDiscounts(params?: any) {
    return this.serviceSetupApi.manageSequentialDiscounts(params);
  }

  public manageServiceOption(params?: any): Observable<any> {
    return this.serviceSetupApi.manageServiceOption(params);
  }

  public manageServiceOptionInclusive(params?: any): Observable<any> {
    return this.serviceSetupApi.manageServiceOptionInclusive(params);
  }

  /**
   * Checks is params.Value is a valid and existing entity
   * The entity depends on params.Type:
   * BkgID -> booking and GroupCod -> Group
   *
   * @param {IResValueExistCheckParams} params
   * @return {Observable<boolean>}
   * @memberof ReservationApiService
   */
  public existsValue(params: IResValueExistCheckParams): Observable<boolean> {
    return this.cashApi.existsValue(params);
  }

  public getDocument(url: string) {
    let headers = this.getHeaders(null);
    let token = localStorage.getItem(LocalStorageKeys.TOKEN);
    if (token) {
      headers = headers.set(LocalStorageKeys.TOKEN, token);
    }

    let options = {
      withCredentials: true,
      headers,
      observe: 'response' as 'body',
      responseType: 'blob' as 'blob'
    };

    let baseUrl = this.urlAPI.substr(0, this.urlAPI.length - 2);
    let apiUrl = baseUrl + '/documents/document?fullPath=' + url;
    return this.http.get(apiUrl, options as any);
  }

  public getInsurances(params: { bkgID: number }): Observable<any[]> {
    return this.insuranceApi.getInsurances(params);
  }

  public getBookedInsurances(params: { bkgID: number, action: string }): Observable<ResInsurance[]> {
    return this.insuranceApi.getBookedInsurances(params);
  }

  public declineInsurance(params?: IReservationApiDeclineInsurance): Observable<any> {
    return this.insuranceApi.declineInsurance(params);
  }
  /**
   * Gets ECM data details for a voyage
   *
   * @param {number} voyageID
   * @return {*}  {Observable<Array<IReservationApiPackageEcmData>>}
   * @memberof ReservationApiService
   */
  getVoyageEcmData(voyageID: number): Observable<Array<IReservationApiPackageEcmData>> {
    return this.inventoryApi.getVoyageEcmData(voyageID);
  }
  /**
   * Gets ECM data details for a package
   *
   * @param {number} packageID
   * @return {*}  {Observable<Array<IReservationApiPackageEcmData>>}
   * @memberof ReservationApiService
   */
  getPackageEcmData(packageID: number): Observable<Array<IReservationApiPackageEcmData>> {
    return this.getDynamicAPI('packageecmdata/get', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, Array<IReservationApiPackageEcmData>>((response) => {
        return response.body as Array<IReservationApiPackageEcmData>;
      }),
      catchError<any, Array<IReservationApiPackageEcmData>>((_: any) => new Array<IReservationApiPackageEcmData>())
    );
  }
  /**
   * Gets data details for a package: ECM, Itineraries and traveldate
   *
   * @param {number} packageID
   * @return {*}  {Observable<IReservationApiPackageInfo>}
   * @memberof ReservationApiService
   */
  getPackageInfo(parameters: IReservationApiPackageInfoParameters): Observable<IReservationApiPackageInfo> {
    return this.packageApi.getPackageInfo(parameters);
  }
  /**
   * Get itinerary of a voyage
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationCruiseItinerary[]>}
   * @memberof ReservationApiService
   */
  getVoyageItinerary(voyageID: number): Observable<ReservationCruiseItinerary[]> {
    return this.inventoryApi.getVoyageItinerary(voyageID);
  }

  //#region Package

  public getPackages(params: IReservationApiGetPackageParams): Observable<ResPackage[]> {
    return this.packageApi.getPackages(params);
  }

  loadDayByDayItinerary(packageID: number) {
    return this.packageApi.loadDayByDayItinerary(packageID);
  }

  manageDayByDayItinerary(params: IResApiManageDayByDayItinerary) {
    return this.packageApi.manageDayByDayItinerary(params);
  }
  dayByDayImportFromWebsite(params: IResApiDayByDayImportFromWebsite) {
    return this.packageApi.dayByDayImportFromWebsite(params);
  }
  dayByDayOperationalImportFromOperational(pkgID: number) {
    return this.packageApi.dayByDayOperationalImportFromOperational(pkgID);
  }
  dayByDayUpdateBkg(params: IResApiDayByDayImportFromWebsite) {
    return this.packageApi.dayByDayUpdateBkg(params);
  }
  queryDayByDayUpdateBkg(params: IResApiDayByDayImportFromWebsite) {
    return this.packageApi.queryDayByDayUpdateBkg(params);
  }
  getPackageOptionCostsAndPrices(params: {
    Currency: string,
    PriceTypeID: number,
    CompanyID: number,
    AgencyID: number | undefined,
    PackageTravelDateID: number,
    StartDate: string,
    EndDate: string,
    PackageID: number,
    ForceReload: 'Y' | 'N',
    PackageOption?: IReservationApiManagePackageOptionsParams[]
  }): Observable<[Array<ResPackageOptionCost>, Array<ResPackagePrice>, Array<ResPackageUpgradePrice>]> {
    return this.packageApi.getPackageOptionCostsAndPrices(params);
  }

  public getPackageSpecialOptions(): Observable<Array<IReservationPackageOptionSpecialItem>> {
    return this.packageApi.getPackageSpecialOptions();
  }

  public managePackage(params: IReservationApiManagePackageParams): Observable<number> {
    return this.packageApi.managePackage(params);
  }

  public managePackageTravelDates(params: IReservationApiManagePackageTravelDatesParams): Observable<any> {
    return this.packageApi.managePackageTravelDates(params);
  }

  getPackageDeparturesFootnotes(): Observable<ResPackageDepartureFootnotes[]> {
    return this.packageApi.getPackageDeparturesFootnotes();
  }

  getPackageDeparturesFreeShorex(): Observable<ResPackageDepartureFreeShorex[]> {
    return this.packageApi.getPackageDeparturesFreeShorex();
  }


  public managePackageElements(params: { PackageID: number, PackageOption: IReservationApiManagePackageOptionsParams[] }): Observable<any> {
    return this.packageApi.managePackageElements(params);
  }

  managePackagePrices(params: {
    PackageTravelDateID: number,
    currency: string,
    PriceTypes?: string,
    PackageOptionCost: IReservationApiPackageOptionCost[],
    PackagePrice: IReservationApiPackageOptionPriceParams[],
    PackageOptionUpgrade: IReservationApiPackageUpgradePriceParams[]
  }, mode: 'old' | 'new'): Observable<any> {
    return this.packageApi.managePackagePrices(params, mode);
  }
  packageManageSingleSupp(params: IResPackageSetupSingleSuppManage): Observable<any> {
    return this.packageApi.packageManageSingleSupp(params);
  }
  public checkPackageOptionPrices(params: { PackageID: number, PackageTravelDateID?: number }): Observable<IReservationApiPackageConfiguratedPrice[]> {
    return this.packageApi.checkPackageOptionPrices(params);
  }

  public packagePricesCloneDates(params: IReservationApiPricesCloneDatesParams): Observable<any> {
    return this.packageApi.packagePricesCloneDates(params);
  }
  public packagePricesCloneUpgrade(params: IReservationApiPricesCloneUpgradeParams): Observable<any> {
    return this.packageApi.packagePricesCloneUpgrade(params);
  }
  public getMultiBooking(params: IReservationApiGetMultiBookingParams): Observable<ResMultiBookingItem[]> {
    return this.bookingApi.getMultiBooking(params);
  }
  public getInvoicePackages(params?: IReservationApiGetInvoicePackagesParams): Observable<ResMultiBookingInvoicePackage[]> {
    return this.bookingApi.getInvoicePackages(params);
  }
  public getMultiBookingPaginated(params: IReservationApiGetMultiBookingParams, loadOptions?: any): Promise<ResolvedData<ResMultiBookingItem>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.callDynamicAPI('item/getMultiBooking', params)).then((response: any) => {
      return {
        data: Array.isArray(response.body) ? response.body.map((row: any) => new ResMultiBookingItem(row)) : [],
        totalCount: response.recordsCount,
      };
    }).catch(() => {
      throw 'Data loading error';
    });
  }
  public createPurchaseOrder(params: IReservationApiCreatePurchaseOrderParams): Observable<IReservationApiCreatePurchaseOrderResult> {
    return this.bookingApi.createPurchaseOrder(params);
  }
  public managePackageItineraryDays(params: IReservationApiManagePackageItineraryDaysParams): Observable<any> {
    return this.packageApi.managePackageItineraryDays(params);
  }

  public managePackageLinkedPorts(params: IReservationApiManagePackageLinkedPortsParams): Observable<any> {
    return this.packageApi.managePackageLinkedPorts(params);
  }

  public managePackageLinkedVoyages(params: IReservationApiManagePackageLinkedVoyagesParams): Observable<any> {
    return this.packageApi.managePackageLinkedVoyages(params);
  }
  public managePackageLinkedTours(params: IReservationApiManagePackageLinkedToursParams): Observable<any> {
    return this.packageApi.managePackageLinkedTours(params);
  }
  public getPackageDepScheduling(packageID: number, history = false): Observable<ResPackageDepScheduling[]> {
    return this.packageApi.getPackageDepScheduling(packageID, history);
  }

  public managePackageDepScheduling(params: IReservationApiManagePackageDepSchedulingParams): Observable<any> {
    return this.packageApi.managePackageDepScheduling(params);
  }

  public getPackageCancPolicy(packageID: number, history = false): Observable<ResPackageCancPolicy[]> {
    return this.packageApi.getPackageCancPolicy(packageID, history);
  }

  public managePackageCancPolicy(params: IReservationApiManangePackageCanPolicyParams): Observable<any> {
    return this.packageApi.managePackageCancPolicy(params);
  }
  public getPackageBookingList(params: { PackageID?: number, PackageTravelDateID?: number | null, BkgStatus?: string }): Observable<ResPackageBookingList[]> {
    return this.packageApi.getPackageBookingList(params);
  }

  getPackageAvailabilityStatus(): Observable<ResPackageAvailabilityStatus[]> {
    return this.packageApi.getPackageAvailabilityStatus();
  }

  public getGuestPreferences(bkgID: number, Id: number): Observable<ResGuestPreference[]> {
    return this.coreApi.getGuestPreferences(bkgID, Id);
  }

  public managePackageAvailability(params: IReservationApiManagePackageAvailabilityParams): Observable<any> {
    return this.packageApi.managePackageAvailability(params);
  }
  /**
   * Calls API to clone the package with specified package ID
   *
   * @param {{ PackageID: number, ClonePrice: ReservationApiBoolean }} params
   * @return {*}  {Observable<number>}
   * @memberof ReservationApiService
   */
  clonePackage(params: IResApiPackageClone): Observable<number> {
    return this.packageApi.clonePackage(params);
  }

  clonePackageSlave(params: IResApiPackageCloneSlave): Observable<number> {
    return this.packageApi.clonePackageSlave(params);
  }

  public getPackageOptionsAvailabilityElements(params: { AvByOption: IReservationApiPackageOptionPriceTypeAvailibilityParams[], AllPriceType: 'Y' | 'N' }): Observable<IReservationApiPackageOptionPriceTypeAvailibility[]> {
    return this.packageApi.getPackageOptionsAvailabilityElements(params);
  }

  public bookPackage(params: IReservationApiBookPackageParams): Observable<any> {
    return this.packageApi.bookPackage(params);
  }

  getPackageUpgrades(params: { BkgID: number }): Observable<ResPackageUpgrade[]> {
    return this.packageApi.getPackageUpgrades(params);
  }

  public managePackageUpgrades(params: IReservationApiManagePackageUpgradeParams): Observable<any> {
    return this.packageApi.managePackageUpgrades(params);
  }

  //#endregion Package


  public getPriceTypes(params: { Type?: 'OD' | 'ChangePriceType', BkgID?: number }): Observable<Array<IReservationPriceType>> {
    return this.bookingApi.getPriceTypes(params);
  }

  public changeODFare(params: { BkgID: number, NEWPriceTypeID: number }): Observable<any> {
    return this.bookingApi.changeODFare(params);
  }

  public getSuiteStatus(params: { VoyageID?: number, PriceTypeID?: number }): Observable<Array<any>> {
    return this.inventoryApi.getSuiteStatus(params);
  }
  public getBookingToRequests(bkgId: number): Observable<BookingToRequest[]> {
    return this.bookingApi.getBookingToRequests(bkgId);
  }
  public getBookingSaleRequests(bkgId: number): Observable<BookingSaleRequest[]> {
    return this.bookingApi.getBookingSaleRequests(bkgId);
  }
  public checkBookingSaleRequests(bkgId: number): Observable<BookingCheckSaleRequest[]> {
    return this.bookingApi.checkBookingSaleRequests(bkgId);
  }
  public saveSuiteStatus(params: { VoyageID: number, PriceTypeID: number, SuiteCategoryCod: string, Status: string }) {
    return this.inventoryApi.saveSuiteStatus(params);
  }

  public getAssignedTo(params: { EntityType?: string, Entity?: number, TouchId?: number }): Observable<Array<{ Cod: string, Desc: string }>> {
    return this.bookingApi.getAssignedTo(params);
  }

  public getAirData(): Observable<ResAirData> {
    return this.rescomApiService.getAirData();
  }
  public getAirDataAll(): Observable<ResAirData> {
    return this.getDynamicAPI('air/get', { IsAllAirport: 'Y' }).pipe(
      map<DynamicApiResponse, ResAirData>((response) => {
        return new ResAirData(response.body);
      })
    );
  }

  public getBkgNotes(params: IReservationApiGetBkgNotes): Observable<Array<ResBkgNote>> {
    return this.bookingApi.getBkgNotes(params);
  }

  public manageBkgNotes(params: IReservationApiManageBkgNotes): Observable<any> {
    return this.bookingApi.manageBkgNotes(params);
  }

  public getBkgTouch(bkgID?: number): Observable<Array<ResBkgNoteTouch>> {
    return this.bookingApi.getBkgTouch(bkgID);
  }

  public getGuestAirData(params: { BkgID: number }): Observable<Array<BookingGuestAirData>> {
    return this.bookingApi.getGuestAirData(params);
  }

  public updateGuestAirData(params: IReservationApiUpdateBookingGuestAirData): Observable<any> {
    return this.bookingApi.updateGuestAirData(params);
  }

  public getSuitePriceCategories(params: { BkgID: number, SectionID: number }): Observable<Array<ResSuitePriceCategory>> {
    return this.bookingApi.getSuitePriceCategories(params);
  }

  public updateSuitePriceCategory(params: IReservationApiUpdateSuiteData): Observable<any> {
    return this.bookingApi.updateSuitePriceCategory(params);
  }

  getPowerBIConfig(workspaceId: string, reportId: string): Observable<any> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'pbi/params',
      data: { workspaceId, reportId },
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    }));
  }

  exportPowerBIReport(workspaceId: string, reportId: string, filters: IFilter[] = []): Observable<Blob> {
    if (!this.pluginUrlAPI) {
      return throwError(() => 'Power BI plugin API URL has not been retrieved yet');
    }

    let headers = this.getHeaders(null);
    if (this.config?.tokenOnHeader) {
      const token = localStorage.getItem(LocalStorageKeys.TOKEN);
      if (token) {
        headers = headers.set(LocalStorageKeys.TOKEN, token);
      }
    }

    return this.http.post(`${this.pluginUrlAPI}/pbi/export`, {
      WorkspaceId: workspaceId,
      ReportId: reportId,
      Filters: filters,
      Format: 'PDF',
      FileName: 'ReportExport.pdf'
    }, {
      headers,
      responseType: 'blob'
    });
  }

  getPowerBIActualCompanies(dashboardName: string, user: string): Observable<string[]> {
    return this.callDynamicAPI(`powerbi/${dashboardName}`, { user }).pipe(
      map<DynamicApiResponse, string[]>((response) =>
        Array.isArray(response.body)
          ? response.body
              .map((item: any) => (typeof item === 'string' ? item : item?.Value))
              .filter((value): value is string => !!value)
          : []
      )
    );
  }

  getDocumentList(containerName: string, prefix: string) {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'documents/document-list',
      data: { containerName, prefix },
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    }));
  }
  /**
   * Get light details of all voyages
   * Used in:
   * - Suite setup page
   *
   * @param {{ VoyageID?: number, MonthList?: string[], ShipID?: number[] }} filters
   * @param {DynamicApiRequestPaginatedParams} [params=new DynamicApiRequestPaginatedParams(1, 150)]
   * @return {*}  {Observable<DynamicApiResponsePaginatedModel<ReservationCruiseLight>>}
   * @memberof ReservationApiService
   */
  getPagedLightVoyages(
    filters: { VoyageID?: number, MonthList?: string[], ShipID?: number[] },
    params: DynamicApiRequestPaginatedParams = new DynamicApiRequestPaginatedParams(1, 150)
  ): Observable<DynamicApiResponsePaginatedModel<ReservationCruiseLight>> {
    return this.productPriceApi.getPagedLightVoyages(filters, params);
  }

  /**
   * Get booked suite of a voyages and their bkgID
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationApiBookedSuite[]>}
   * @memberof ReservationApiService
   */
  getVoyageSuiteAndBookingList(voyageID: number, amenities: IReservationSuiteAmenity[] = [], includeWL: boolean = true): Observable<ReservationCruiseSuite[]> {
    return this.inventoryApi.getVoyageSuiteAndBookingList(voyageID, amenities, includeWL);
  }

  getVoyageGroupList(voyageID?: number): Observable<any[]> {
    return this.inventoryApi.getVoyageGroupList(voyageID);
  }


  getAirLines(): Observable<IReservationApiAirLine[]> {
    return this.airApi.getAirLines();
  }
  /**
   * Get voyage suite profitability
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationCruiseSuiteProfitability[]>}
   * @memberof ReservationApiService
   */
  getVoyageSuiteProfitability(voyageID: number, priceTypeID: number): Observable<ReservationCruiseSuiteProfitability[]> {
    return this.inventoryApi.getVoyageSuiteProfitability(voyageID, priceTypeID);
  }

  getRightRezUrl(params: ReservationApiRightRezParams): Observable<ReservationApiRightRezResponse> {
    return this.airApi.getRightRezUrl(params);
  }
  getAirResults(params: IReservationApiGetAirResultsParams): Observable<{ itineraries: ReservationAirResult[], Error?: string }> {
    return this.airApi.getAirResults(params);
  }

  bookAirResult(params: IReservationApiBookAirResultParams): Observable<{ pnr: IReservationApiAirPnr, Error?: string }> {
    return this.airApi.bookAirResult(params);
  }

  getAirPnrs(bookingId: number): Observable<ReservationAirPnr[]> {
    return this.bookingApi.getAirPnrs(bookingId);
  }
  deletePnrDocument(flightHeaderId: number) {
    return this.airApi.deletePnrDocument(flightHeaderId);
  }
  uploadPnrDocument(params: ReservationUploadBlobResponse, flightHeaderId: number) {
    return this.airApi.uploadPnrDocument(params, flightHeaderId);
  }
  getAirPnrHistory(params: IReservationApiGetAirPnrHistoryParams): Observable<ReservationAirPnrHistory[]> {
    return this.airApi.getAirPnrHistory(params);
  }
  /**
   * Import external A&K TravelStudio airs in OneIS
   *
   * @param {number} bkgID
   * @param {{ BkgID: string, SiteID: number }[]} params
   * @return {*}  {Observable<any>}
   * @memberof ReservationApiService
   */
  importExternalAirs(bkgID: number, params: { BkgID: string, SiteID: number }[]): Observable<any> {
    return this.bookingApi.importExternalAirs(bkgID, params);
  }
  releaseOrDeleteAirRRPnr(params: IReservationApiDeleteAirPnrParams): Observable<string> {
    return this.bookingApi.releaseOrDeleteAirRRPnr(params);
  }
  deleteAirInternalFlight(params: IReservationApiDeleteAirPnrParams): Observable<string> {
    return this.airApi.deleteAirInternalFlight(params);
  }
  deleteAirRRPnr(params: IReservationApiDeleteAirPnrParams, isTicketed: boolean): Observable<string> {
    return this.bookingApi.deleteAirRRPnr(params, isTicketed);
  }

  deleteAirPnr(params: IReservationApiDeleteAirPnrParams, isTicketed: boolean): Observable<string> {
    return this.bookingApi.deleteAirPnr(params, isTicketed);
  }

  refreshAirPnrs(params: IReservationApiRefreshAirResultPnrsParams): Observable<{ pnrs: ReservationAirPnr[], Error?: string }> {
    return this.bookingApi.refreshAirPnrs(params);
  }
  refreshAirRRPnrs(params: IReservationApiRefreshAirResultPnrsParams): Observable<{ pnrs: ReservationAirPnr[], Error?: string }> {
    return this.bookingApi.refreshAirRRPnrs(params);
  }

  importFromPNR(params: IReservationApiImportPNRParams): Observable<{ Error?: string }> {
    return this.bookingApi.importFromPNR(params);
  }


  changePnrAirClass(params: IReservationApiChangePnrAirClass): Observable<any> {
    return this.bookingApi.changePnrAirClass(params);
  }

  getLoyaltyRedemptionTiers(): Observable<IReservationLoyaltyRedemptionTier[]> {
    return this.callDynamicAPI('redemptionTiers/select', undefined, 'GET').pipe(
      map<DynamicApiResponse, IReservationLoyaltyRedemptionTier[]>((response) =>
        response.body
      )
    );
  }

  searchCertificates(params: IResApiSearchCertificate): Observable<ResBookingCertificate[]> {
    return this.bookingApi.searchCertificates(params);
  }

  importCertificates(params: IResApiImportCertificate): Observable<any> {
    return this.bookingApi.importCertificates(params);
  }

  getBkgCertificates(bkgID: number): Observable<ResBookingCertificate[]> {
    return this.bookingApi.getBkgCertificates(bkgID);
  }

  getBkgCertificateDetail(params: IResApiGetBookingCertificateDetail): Observable<ResBookingCertificateDetail[]> {
    return this.bookingApi.getBkgCertificateDetail(params);
  }

  getBkgCertificateTypes(): Observable<ResCertificateType[]> {
    return this.bookingApi.getBkgCertificateTypes();
  }

  createBkgCertificate(params: IResApiCreateBookingCertificate): Observable<any> {
    return this.bookingApi.createBkgCertificate(params);
  }

  updateBkgCertificate(params: IResApiUpdateBookingCertificate): Observable<any> {
    return this.postDynamicAPI('bkgCertificate/update', params);
  }

  getManifestVoyages({ VoyageNumber }: { VoyageNumber: string }): Observable<IResApiManifestVoyage[]> {
    return this.callDynamicAPI('manifestVoyage/select', { VoyageNumber }, 'GET').pipe(
      map<DynamicApiResponse, IResApiManifestVoyage[]>((response) =>
        response.body
      )
    );
  }

  getManifest(params: IResApiGetManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    return this.manifestApi.getManifest(params, loadOptions);
  }

  getManifestGroupBy(params: IResApiGetManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    return this.manifestApi.getManifestGroupBy(params, loadOptions);
  }

  getManifestGroupByPaginated(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    params = this.fillPaginatedParams(params, loadOptions);
    const url = params.PackageID && params.PackageID > 0 ? 'manifest/getManifestGroupPkg' : 'manifestGroupBy/select';
    return lastValueFrom(this.callDynamicAPI(url, params)).then((response: any) => {
      return {
        data: response.body.map((manifestGroup: any) => new ResManifest(manifestGroup)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getManifestGroupByPaginatedPR(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    return this.manifestApi.getManifestGroupByPaginatedPR(params, loadOptions);
  }

  getManifestGroupByPaginatedPKG(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    return this.manifestApi.getManifestGroupByPaginatedPKG(params, loadOptions);
  }

  getSavedManifest(params: IResApiGetSavedManifestsParams | IResApiGetSavedFullManifestsParams, loadOptions: any, isFullManifest: boolean = false): Observable<{ data: ResSavedManifestHeader[], totalCount: number }> {
    return this.manifestApi.getSavedManifest(params, loadOptions, isFullManifest);
  }

  openSavedManifest(params: IResApiOpenSavedManifestParams | IResApiOpenSavedFullManifestParams, loadOptions: any, isFullManifest: boolean = false): Observable<{ data: ResManifest[], totalCount: number }> {
    return this.manifestApi.openSavedManifest(params, loadOptions, isFullManifest);
  }

  saveManifest(params: IResApiSaveManifestParams | IResApiSaveFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    return this.manifestApi.saveManifest(params, isFullManifest);
  }

  updateManifest(params: IResApiUpdateManifestParams | IResApiUpdateFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    return this.manifestApi.updateManifest(params, isFullManifest);
  }

  updateManifestHeader(params: IResApiUpdateManifestHeaderParams | IResApiUpdateFullManifestHeaderParams, isFullManifest: boolean = false): Observable<any> {
    return this.manifestApi.updateManifestHeader(params, isFullManifest);
  }

  deleteManifest(params: IResApiDeleteManifestParams | IResApiDeleteFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    return this.manifestApi.deleteManifest(params, isFullManifest);
  }

  compareManifest(params: IResApiCompareManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    return this.manifestApi.compareManifest(params, loadOptions);
  }

  compareTwoManifest(params: IResApiCompareTwoManifestsParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    return this.manifestApi.compareTwoManifest(params, loadOptions);
  }



  //

  getBkgAirDetails(params: { BkgID: number }): Observable<ResBkgAirDetail[]> {
    return this.packageApi.getBkgAirDetails(params);
  }

  getBkgWishlist(params: { BkgID: number }): Observable<ResBkgWishlist[]> {
    return this.bookingApi.getBkgWishlist(params);
  }

  manageBkgWishlist(params: IReservationApiManageBkgWishlistParams): Observable<any> {
    return this.bookingApi.manageBkgWishlist(params);
  }

  getDecks(params: { Wishlist: ReservationApiBoolean }): Observable<ResDeck[]> {
    return this.bookingApi.getDecks(params);
  }

  getCommissionPayout(params: IResApiGetCommissionPayoutParams, loadOptions: any): Observable<{ data: ResCommissionPayout[], totalCount: number }> {
    return this.commissionApi.getCommissionPayout(params, loadOptions);
  }

  getCommissionPayoutAK(params: IResApiGetCommissionPayoutParams, loadOptions: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number }> {
    return this.commissionApi.getCommissionPayoutAK(params, loadOptions);
  }

  createCommissionPayoutBatch(params: IResApiCreateCommissionPayoutBatchParams): Observable<any> {
    return this.commissionApi.createCommissionPayoutBatch(params);
  }

  createCommissionPayoutBatchAK(params: IResApiCreateCommissionPayoutBatchParams): Observable<any> {
    return this.commissionApi.createCommissionPayoutBatchAK(params);
  }

  getCommissionPayoutBatches(params: IResApiGetCommissionPayoutBatchesParams, loadOptions: any): Observable<{ data: ResCommissionPayoutBatch[], totalCount: number }> {
    return this.commissionApi.getCommissionPayoutBatches(params, loadOptions);
  }

  getCommissionPayoutBatchesAK(params: IResApiGetCommissionPayoutBatchesParams, loadOptions: any): Observable<{ data: ResCommissionPayoutBatch[], totalCount: number }> {
    return this.commissionApi.getCommissionPayoutBatchesAK(params, loadOptions);
  }

  openCommissionPayoutBatch(params: IResApiOpenCommissionPayoutBatchParams, loadOptions: any): Observable<{ data: ResCommissionPayout[], totalCount: number }> {
    return this.commissionApi.openCommissionPayoutBatch(params, loadOptions);
  }

  openCommissionPayoutBatchAK(params: IResApiOpenCommissionPayoutBatchParams, loadOptions: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number }> {
    return this.commissionApi.openCommissionPayoutBatchAK(params, loadOptions);
  }

  manageCommissionPayoutBatch(params: IResApiManageCommissionPayoutBatchParams): Observable<any> {
    return this.commissionApi.manageCommissionPayoutBatch(params);
  }

  manageCommissionPayoutBatchAK(params: IResApiManageCommissionPayoutBatchParams): Observable<any> {
    return this.commissionApi.manageCommissionPayoutBatchAK(params);
  }


  deleteCommissionPayout(params: IResApiDeleteCommissionPayoutParams): Observable<any> {
    return this.commissionApi.deleteCommissionPayout(params);
  }

  deleteCommissionPayoutAK(params: IResApiDeleteCommissionPayoutParams): Observable<any> {
    return this.commissionApi.deleteCommissionPayoutAK(params);
  }

  checkCommissionPayoutBatch(params: IResApiCheckCommissionPayoutBatchParams): Observable<{ BkgID: number, CommissionPayoutDetID: number }[]> {
    return this.commissionApi.checkCommissionPayoutBatch(params);
  }

  checkCommissionPayoutBatchAK(params: IResApiCheckCommissionPayoutBatchParams): Observable<{ BkgID: number, CommissionPayoutDetID: number }[]> {
    return this.commissionApi.checkCommissionPayoutBatchAK(params);
  }

  sendCommissionPayoutBatchToFinance(params: IResApiSendCommissionPayoutBatchToFinanceParams): Observable<IResApiSendCommissionPayoutEmailSpecs | undefined> {
    return this.commissionApi.sendCommissionPayoutBatchToFinance(params);
  }

  sendCommissionPayoutBatchToFinanceAK(params: IResApiSendCommissionPayoutBatchToFinanceParams): Observable<IResApiSendCommissionPayoutEmailSpecs | undefined> {
    return this.commissionApi.sendCommissionPayoutBatchToFinanceAK(params);
  }

  uploadCommissionPayoutBatchPayment(params: IResApiUploadCommissionPayoutBatchPaymentParams): Observable<any> {
    return this.commissionApi.uploadCommissionPayoutBatchPayment(params);
  }

  uploadCommissionPayoutBatchPaymentAK(params: IResApiUploadCommissionPayoutBatchPaymentParams): Observable<any> {
    return this.commissionApi.uploadCommissionPayoutBatchPaymentAK(params);
  }

  generateCommissionPayoutBatchSelfInvoice(params: IResApiGenerateCommissionPayoutBatchSelfInvoiceParams): Observable<any> {
    return this.commissionApi.generateCommissionPayoutBatchSelfInvoice(params);
  }

  generateCommissionPayoutBatchSelfInvoiceAK(params: IResApiGenerateCommissionPayoutBatchSelfInvoiceParams): Observable<any> {
    return this.commissionApi.generateCommissionPayoutBatchSelfInvoiceAK(params);
  }

  sendCommissionPayoutBatchToBc(params: IResApiSendCommissionPayoutBatchToBcParams): Observable<any> {
    return this.commissionApi.sendCommissionPayoutBatchToBc(params);
  }

  sendCommissionPayoutBatchToBcAK(params: IResApiSendCommissionPayoutBatchToBcParams): Observable<any> {
    return this.commissionApi.sendCommissionPayoutBatchToBcAK(params);
  }

  unlockComissionPayoutBkgs(params: IResApiUnlockCommissionPayoutBkgsParams): Observable<any> {
    return this.commissionApi.unlockComissionPayoutBkgs(params);
  }

  unlockComissionPayoutBkgsAK(params: IResApiUnlockCommissionPayoutBkgsParams): Observable<any> {
    return this.commissionApi.unlockComissionPayoutBkgsAK(params);
  }

  completeCommissionPayoutBatch(params: { batchID: number }): Observable<any> {
    return this.commissionApi.completeCommissionPayoutBatch(params);
  }

  completeCommissionPayoutBatchAK(params: { batchID: number }): Observable<any> {
    return this.commissionApi.completeCommissionPayoutBatchAK(params);
  }

  getCommissionPayoutBatchAgencies(params: IResApiGetCommissionPayoutAgenciesParams): Observable<ReservationAgency[]> {
    return this.commissionApi.getCommissionPayoutBatchAgencies(params);
  }

  getCommissionPayoutBatchAgenciesAK(params: IResApiGetCommissionPayoutAgenciesParams): Observable<ReservationAgency[]> {
    return this.commissionApi.getCommissionPayoutBatchAgenciesAK(params);
  }

  getCommissionTypesAK(): Observable<IReservationCommissionPayoutType[]> {
    return this.commissionApi.getCommissionTypesAK();
  }

  getCommissionPayoutBkgList(params: IResApiGetCommissionPayoutBkgListParams, loadOptions?: any): Observable<{ data: ResCommissionPayout[], totalCount: number } | ResCommissionPayout[]> {
    return this.commissionApi.getCommissionPayoutBkgList(params, loadOptions);
  }

  getCommissionPayoutBkgListAK(params: IResApiGetCommissionPayoutBkgListParams, loadOptions?: any): Observable<{ data: ResCommissionPayoutAK[], totalCount: number } | ResCommissionPayoutAK[]> {
    return this.commissionApi.getCommissionPayoutBkgListAK(params, loadOptions);
  }

  getCommissionPayoutCompanies(): Observable<ResCommissionCompanies[]> {
    return this.commissionApi.getCommissionPayoutCompanies();
  }



  // #region Promo Setup

  public deletePromo(params?: { PromoID: number }) {
    return this.promoApi.deletePromo(params);
  }

  public getCombinability(): Observable<{ Value: number, DisplayValue: string }[]> {
    return this.callDynamicAPI('promo/combinability').pipe(
      map<DynamicApiResponse, Array<{ Value: number, DisplayValue: string }>>((response) => {
        return response.body.map(
          (combinability: IPromoCombinability) => {
            return { Value: combinability.PromoCombinabilityID, DisplayValue: combinability.PromoCombinability }
          }
        );
      })
    );
  }

  public getPromos(
    params: { PromoID: number }
  ): Observable<ResPromo[]> {
    return this.promoApi.getPromos(params);
  }


  public getApplyPromo(
    params: IReservationPromoApplyPramaters
  ): Observable<ResPromoApply[]> {

    // if(!params.BkgID && !params.PackageID && !params.VoyageID && !params.PromoID && !params.IndividualID) return of([])
    if (!params.VoyageID && !params.PackageID && !params.PackageTravelDateID && !params.PromoID && !params.BkgID) return of([])

    return this.callDynamicAPI('promo-apply/get', params).pipe(
      map<DynamicApiResponse, Array<ResPromoApply>>((response) => {
        if (!response.body.Promos) return []; //@TODO display error configuration
        return response.body.Promos.map(
          (p: any) => new ResPromoApply(p)
        );
      })
    );
  }

  public bkgPromoItemsManage(
    params: IReservationBkgPromoItemManagePramaters
  ): Observable<boolean> {
    return this.promoApi.bkgPromoItemsManage(params);
  }
  public managePromo(params: IReservationApiManagePromoParams): Observable<number> {
    return this.promoApi.managePromo(params);
  }

  public getPromoItems(params: { PromoID?: number }
  ): Observable<ResPromoActionItems> {
    return this.promoApi.getPromoItems(params);
  }

  public getBonusCommissionType(params: { PromoID?: number }): Observable<ResBonusCommissionType[]> {
    return this.promoApi.getBonusCommissionType(params);
  }

  public savePromoAction(params: IReservationApiManagePromoActionParams): Observable<void> {
    return this.promoApi.savePromoAction(params);
  }

  public getPromoVoyages(params: { PromoID?: number }
  ): Observable<Array<ReservationCruise>> {
    return this.promoApi.getPromoVoyages(params);
  }

  getPromoCompatibility(params: { PromoID?: number }): Observable<Array<ResPromoCompatibility>> {
    return this.promoApi.getPromoCompatibility(params);
  }

  public getPromoCompanies(params: { Promo?: string }
  ): Observable<ResPromoCompany[]> {
    return this.promoApi.getPromoCompanies(params);
  }

  public getPromoSuiteCategories(params: { promoId?: number }
  ): Observable<ResPromoSuiteCategory[]> {
    return this.promoApi.getPromoSuiteCategories(params);
  }

  public getPromotionHeader(params: { promoID?: number }): Observable<ResPromoHeader[]> {
    return this.promoApi.getPromotionHeader(params);
  }

  public savePromoVoyages(params: IReservationApiManagePromoVoyagesParams): Observable<void> {
    return this.promoApi.savePromoVoyages(params);
  }

  public saveCompatiblePromo(params: IReservationApiManagePromoCompatibilityParams): Observable<void> {
    return this.promoApi.saveCompatiblePromo(params);
  }

  public getPromoAgencies(params: { PromoID?: number }
  ): Observable<Array<ReservationAgency | ReservationIndividual>> {
    return this.promoApi.getPromoAgencies(params);
  }

  public savePromoAgencies(params: IReservationApiManagePromoAgenciesParams): Observable<void> {
    return this.promoApi.savePromoAgencies(params);
  }

  getPromoBooking(promoID: number): Observable<ResGetPromoBooking[]> {
    return this.getDynamicAPI('promo/getPromoBooking', { PromoID: promoID }).pipe(
      map<DynamicApiResponse, ResGetPromoBooking[]>((response) => {
        return response.body.map((data: any) => new ResGetPromoBooking(data))
      })
    );
  }
  // #endregion Promo Setup


  // Create or Modify a city inside Destination Geo Tree Tab
  public manageGeoTreeDestination(params: IReservationApiGeotree): Observable<any> {
    return this.callDynamicAPI('geotree/treeManage', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public loadDestinationPopup(orderType?: number, geoTreeID?: number): Observable<GeoTreeSearch[]> {
    return this.callDynamicAPI('geotree/FilterGet', { orderType: orderType, GeoTreeId: geoTreeID }).pipe(
      map<DynamicApiResponse, GeoTreeSearch[]>((response) => {
        return response.body.map((r: any) => new GeoTreeSearch(r))
      })
    )
  }
  public getBookingMarginality(bkgID?: number): Observable<ReservationBookingMarginality> {
    return this.bookingApi.getBookingMarginality(bkgID);
  }

  public getMarkets(): Observable<Array<Market>> {
    return this.bookingApi.getMarkets();
  }

  createNewServiceOptionPackage(params: IReservationApiCreateNewServiceOrOption) {
    return this.packageApi.createNewServiceOptionPackage(params);
  }

  public getSearchProducts(): Observable<ResProductCod[]> {
    return this.productPriceApi.getSearchProducts();
  }

  public getPromoPaginated(params?: IPromoParams, loadOptions?: any): Promise<ResolvedData<ResPromo>> {
    return this.promoApi.getPromoPaginated(params, loadOptions);
  }

  public getCashReceiptsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ResCashReceipt>> {
    return this.cashApi.getCashReceiptsPaginated(params, loadOptions);
  }
  getSearchFilterPreferences(companyID?: number): Observable<ResSearchFilterPreferences> {
    return this.callDynamicAPI('search/getFilterPreferences', { CompanyID: companyID }).pipe(
      map<DynamicApiResponse, ResSearchFilterPreferences>((response) => {
        return new ResSearchFilterPreferences(response.body);
      })
    )
  }
  getSearchFilterToggleFavourite(section: string, item: ResSearchFilterSmartItem, favourite: boolean): Observable<boolean> {
    return this.productPriceApi.getSearchFilterToggleFavourite(section, item, favourite);
  }
  getSearchSmartFilterPage(params: IResSearchSmartFilter) {
    return this.productPriceApi.getSearchSmartFilterPage(params);
  }
  public getPromo(params?: IPromoParams) {
    return this.postDynamicAPI('promo/search', params).pipe(
      map<DynamicApiResponse, Array<ResPromo>>((response) => {
        return response.body.map((promo: any) => new ResPromo(promo));
      })
    );
  }

  //Topic Tab
  getTopicItinerary(params?: IResApiGetGeoTreeTopicItinerary): Observable<Array<ResTopicItinerary>> {
    return this.productPriceApi.getTopicItinerary(params);
  }
  //Visa & Health Tab
  getVisaHealth(packageID?: number): Observable<Array<ResBkgVisaHealth>> {
    return this.callDynamicAPI('bkg/getVisaHealth', { ProductID: packageID }).pipe(
      map<DynamicApiResponse, Array<ResBkgVisaHealth>>((response) => {
        return response.body.map((data: any) => new ResBkgVisaHealth(data));
      })
    )
  }
  getVisaHealthBkg(bkgId: number): Observable<Array<ResBkgVisaHealth>> {
    return this.productPriceApi.getVisaHealthBkg(bkgId);
  }
  //Voyage Description Tab
  getVoyageDescription(voyageID?: number): Observable<ResVoyageDescription> {
    return this.productPriceApi.getVoyageDescription(voyageID);
  }

  //Booking Journey Summary Itinerary
  getBkgJourneyItinerary(params: IResApiGetBkgJourneyItineraryParams): Observable<Array<ResBkgJourneyItinerary>> {
    return this.bookingApi.getBkgJourneyItinerary(params);
  }

  getNoteTypes(bkgID?: number): Observable<IReservationNoteType[]> {
    return this.bookingApi.getNoteTypes(bkgID);
  }
  getCabinOccupancy(params: IResGetCabinOccupancyParams): Observable<ReservationCabinOccupancy[]> {
    return this.getDynamicAPI('voyage/cabinOccupancy', params).pipe(
      map<DynamicApiResponse, ReservationCabinOccupancy[]>((response) => {
        return response.body.map((data: any) => new ReservationCabinOccupancy(data))
      })
    );
  }

  manageBkgJourneyItinerary(params: IResApiManageBkgJourneyItinerary) {
    return this.bookingApi.manageBkgJourneyItinerary(params);
  }

  public getPackagesPaginated(params?: IReservationApiGetPackageParams, loadOptions?: any): Promise<ResolvedData<ResPackage>> {
    return this.packageApi.getPackagesPaginated(params, loadOptions);
  }

  getBkgDmc(bkgID: number): Observable<Array<ResBkgDmc>> {
    return this.bookingApi.getBkgDmc(bkgID);
  }

  getDmcManage(bkgID: number): Observable<any> {
    return this.bookingApi.getDmcManage(bkgID);
  }

  getFinancialSplit(bkgID: number, section: string): Observable<Array<ResFinancialSplit>> {
    return this.bookingApi.getFinancialSplit(bkgID, section);
  }

  getFinancialSplitMultiCurrency(bkgID: number): Observable<Array<ResFinancialSplitMultiCurrency>> {
    return this.bookingApi.getFinancialSplitMultiCurrency(bkgID);
  }

  getPaymentMilestone(bkgID: number): Observable<Array<ResPaymentMilestone>> {
    return this.bookingApi.getPaymentMilestone(bkgID);
  }

  managePaymentMilestone(params: IResManagePaymentMilestoneParams): Observable<any> {
    return this.bookingApi.managePaymentMilestone(params);
  }

  getMarketingStatus(): Observable<MarketingStatus[]> {
    return this.packageApi.getMarketingStatus();
  }

  getHlTypes(): Observable<HlTypes[]> {
    return this.packageApi.getHlTypes();
  }

  getPackageHighlightsType(): Observable<PackageHighlightType[]> {
    return this.packageApi.getPackageHighlightsType();
  }

  getPackageHighlightsList(packageID: number): Observable<PackageHighlight[]> {
    return this.packageApi.getPackageHighlightsList(packageID);
  }

  getBookingCreatedBy(bookingID: number): Observable<BookingCreatedBy[]> {
    return this.bookingApi.getBookingCreatedBy(bookingID);
  }

  managePackageHighlights(packageID: number, packageHighlights: IPackageHighlight[]) {
    return this.packageApi.managePackageHighlights(packageID, packageHighlights);
  }

  getPackageItineraryDays2AKP(packageID: number, packageItineraryDayID: number): Observable<PackageItineraryDay2AKP[]> {
    return this.packageApi.getPackageItineraryDays2AKP(packageID, packageItineraryDayID);
  }

  managePackageItineraryDays2AKP(packageID: number, packageItineraryDay2AKP: IPackageItineraryDay2AKP[]) {
    return this.packageApi.managePackageItineraryDays2AKP(packageID, packageItineraryDay2AKP);
  }

  getPackageDayLocationsByProjectID(params?: IPackageDayLocationsByProject): Observable<PackageDayLocation[]> {
    return this.packageApi.getPackageDayLocationsByProjectID(params);
  }

  getPackageDayProjects(params?: IPackageDayProjects): Observable<PackageDayProject[]> {
    return this.packageApi.getPackageDayProjects(params);
  }

  //Edit Bkg Page

  getSalesAssistant(bkgID?: number): Observable<ResBkgSalesAssistant[]> {
    return this.bookingApi.getSalesAssistant(bkgID);
  }

  getSecondaryTravelAgency(bkgID?: number): Observable<ResBkgSecondaryTravelAgency[]> {
    return this.bookingApi.getSecondaryTravelAgency(bkgID);
  }

  getSecondarySalesAgent(agencyCod?: string): Observable<ResBkgSecondarySalesAgent[]> {
    return this.bookingApi.getSecondarySalesAgent(agencyCod);
  }

  getSegment(): Observable<ResBkgSegment[]> {
    return this.bookingApi.getSegment();
  }

  getSubSegment(segmentID?: number): Observable<ResBkgSubSegment[]> {
    return this.bookingApi.getSubSegment(segmentID);
  }

  getMainDestination(): Observable<ResBkgMainDestination[]> {
    return this.bookingApi.getMainDestination();
  }

  //Download Excel from Package Setup
  getExportedPackageTable(params: { PackageID?: number, PackageTravelDateID?: number, BkgStatus?: string }): Observable<ResPackageExportListByBooking[]> {
    return this.packageApi.getExportedPackageTable(params);
  }

  getTourBook(tourBookParams: IResApiGetTourBookParams): Observable<TourBook[]> {
    return this.productPriceApi.getTourBook(tourBookParams);
  }

  getServiceECM(serviceID?: number): Observable<ResServiceEMCDetails[]> {
    return this.serviceSetupApi.getServiceECM(serviceID);
  }

  getConnectedPackage(params: IResApiPackageConnectedPackages): Observable<ResPackageConnectedPackages[]> {
    return this.packageApi.getConnectedPackage(params);
  }

  //Package Travel date guide
  getPackageTravelDateGuide(params?: IResApiGetPackageTravelDateGuide): Observable<ResPackageTravelDateGuide[]> {
    return this.peopleApi.getPackageTravelDateGuide(params);
  }

  managePackageTravelDateGuide(params: IResManagePackageTravelDateGuide) {
    return this.peopleApi.managePackageTravelDateGuide(params);
  }

  //Package Suppliers Contacts
  getCommunicationEmail(params: IResApiPackageCommunicationEmail): Observable<ResPackageCommunicationEmail[]> {
    return this.packageApi.getCommunicationEmail(params);
  }

  //Package Suppliers Contacts
  getSupplierRequestData(params: IResApiPackageSupplierRequest): Observable<ResPackageSupplierRequestData> {
    return this.supplierApi.getSupplierRequestData(params);
  }

  getSupplierEmailRequestURL(params: IResApiPackageSupplierRequest): Observable<ResPackageSupplierEmailRequestURL[]> {
    return this.serviceSetupApi.getSupplierEmailRequestURL(params);
  }

  manageCommunicationEmail(params: IResApiPackageCommunicationEmail) {
    return this.packageApi.manageCommunicationEmail(params);
  }

  saveSupplierRequest(params: IResApiSupplierRequest) {
    return this.supplierApi.saveSupplierRequest(params);
  }

  //Get Package Footnote
  getPackageFootnote(packageID: number): Observable<ResPackageFootnotes[]> {
    return this.packageApi.getPackageFootnote(packageID);
  }

  guestsManage(params: IResApiGuestsManage): Observable<any[]> {
    return this.peopleApi.guestsManage(params);
  }
  checkGuestActivity(bkgID?: number, guestCod?: string): Observable<any[]> {
    return this.peopleApi.checkGuestActivity(bkgID, guestCod);
  }

  getPackageType(): Observable<ResPackageType[]> {
    return this.productPriceApi.getPackageType();
  }

  getPackageTypeForSetup(): Observable<ResPackageType[]> {
    return this.packageApi.getPackageTypeForSetup();
  }

  getPackageVoyageSuites(packageID: number, packageTravelDateID: number): Observable<ResPackageSuiteConfig[]> {
    return this.packageApi.getPackageVoyageSuites(packageID, packageTravelDateID);
  }

  getVillaAvailability(params: IResVillaAvailabilityParams): Observable<ResVillaAvailability[]> {
    return this.bookingApi.getVillaAvailability(params);
  }

  experienceAvailabilityGet(params: IResExpAvailabilityGetParams): Observable<ResExpAvailability> {
    return this.productPriceApi.experienceAvailabilityGet(params);
  }
  getExperienceDetail(experienceID: number): Observable<ResExperience> {
    return this.productPriceApi.getExperienceDetail(experienceID);
  }
  getExperienceOptional(bkgID: number, experienceID: number): Observable<ResExperienceOptional[]> {
    return this.bookingApi.getExperienceOptional(bkgID, experienceID);
  }
  experienceBook(params: ResBkgExperiencesApiParams) {
    return this.productPriceApi.experienceBook(params);
  }
  experienceManageChoices(params: ResBkgExperienceManageChoicesApiParams) {
    return this.serviceSetupApi.experienceManageChoices(params);
  }

  getJets(packageTypeID?: number): Observable<ResPackageJets[]> {
    return this.packageApi.getJets(packageTypeID);
  }

  getPackageJetSeats(params: IResApiPackageGetJetSeats): Observable<ResPackageJetSeat[]> {
    return this.packageApi.getJetSeats(params);
  }

  managePackageJetSeats(params: IResApiPackageManageJetSeats) {
    return this.packageApi.managePackageJetSeats(params);
  }

  getBkgProductTypes(JSONCompanyID?: number): Observable<ResBkgProductTypes[]> {
    return this.productPriceApi.getBkgProductTypes(JSONCompanyID);
  }

  getPackagePricesSetup(params: { PackageID: number, CompanyIDTM: number, StartDate?: DateTime }): Observable<{ packageHeader: ResPackagePriceHeader; prices: ResPackagePriceSetup[]; travelDates: any[], currencies: string[], priceTypes: ResPackagePriceSetupDropdown[], validityPeriods: ResPackagePriceValidityPeriod[], priceNoteTypes: IResPriceNote[], packageDisplayTypes: IResPackageDisplay[], companies: IReservationCompany[], voyages: { voyageID: number, voyageNumber: string }[] }> {
    return this.packageApi.getPackagePricesSetup(params);
  }

  managePackagePricesSetup(params: IResApiManagePackagePricesSetup) {
    return this.packageApi.managePackagePricesSetup(params);
  }

  checkBookingDayByDay(BkgID: number) {
    return this.bookingApi.checkBookingDayByDay(BkgID);
  }

  checkBookingDayByDayForProposal(BkgID: number): Observable<{ hasDayByDay: boolean, hasPersonalization: boolean, hasGeneralInclusions: boolean }> {
    return this.bookingApi.checkBookingDayByDayForProposal(BkgID);
  }

  getBkgDeleteMessage(bkgID?: number): Observable<string> {
    return this.bookingApi.getBkgDeleteMessage(bkgID);
  }

  checkRoomAvailability(_params: IResRoomAvailabilityCheckParams): Observable<boolean> {
    return this.bookingApi.checkRoomAvailability(_params);
  }

  deleteBookingPreCheck(bkgID?: number, section: 'CancelBooking' | 'ChangeVoyage' = 'CancelBooking'): Observable<{ MessageCod: string, MessageDesc: string }[]> {
    return this.bookingApi.deleteBookingPreCheck(bkgID, section);
  }

  getPaymentDisclaimerMessage(): Observable<string> {
    return this.cashApi.getPaymentDisclaimerMessage();
  }

  getBonusCommissionDetail(bkgID?: number): Observable<ResBonusCommissionDetail[]> {
    return this.commissionApi.getBonusCommissionDetail(bkgID);
  }

  getPackageProperty(): Observable<ResPackageProperty[]> {
    return this.packageApi.getPackageProperty();
  }

  managePromoItems(params: any) {
    return this.postDynamicAPI('promo/manageItems', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  managePromoAgenciesAll(selectAll: boolean, promoID: number) {
    return this.postDynamicAPI('promo/manageAllPromoAgencies', { All: selectAll, PromoID: promoID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    );
  }

  getPromoProperties(promoID: number): Observable<ResPromoProperty[]> {
    return this.promoApi.getPromoProperties(promoID);
  }

  getSearchablePromoProperties(params: IResApiSearchablePromoProperties, loadOptions?: any): Promise<ResolvedData<ResPromoProperty>> {
    return this.promoApi.getSearchablePromoProperties(params, loadOptions);
  }


  managePromoProperties(params?: IResApiManagePromoProperties) {
    return this.promoApi.managePromoProperties(params);
  }

  getSupplierContacts(params: IResApiSupplierContacts): Observable<ResSupplierContact[]> {
    return this.supplierApi.getSupplierContacts(params);
  }

  getBkgTailorMadeParams(bkgID?: number): Observable<{ params: ResBkgTailorMadeParams, serviceTypes: ResBkgTailorMadeServiceTypes[] }> {
    return this.callDynamicAPI('bkg/getTMParams', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, { params: ResBkgTailorMadeParams, serviceTypes: ResBkgTailorMadeServiceTypes[] }>((response) => {
        return {
          params: response.body.DT0.map((data: any) => new ResBkgTailorMadeParams(data))[0],
          serviceTypes: response.body.DT1.map((data: any) => new ResBkgTailorMadeServiceTypes(data))
        };
      })
    )
  }
  saveBkgTailorMade(bkgID: number, items: IResTailorMadeItems[]): Observable<any> {
    const params = { BkgID: bkgID, TMSrc: items }
    return this.postDynamicAPI('bkg/tmManage', params).pipe(
      map<DynamicApiResponse, { params: ResBkgTailorMadeParams, serviceTypes: ResBkgTailorMadeServiceTypes[] }>((response) => {
        return Array.isArray(response.body) ? response.body[0] : (response.body?.DT0?.[0] ?? 'OK')
      })
    )
  }

  getPackageCategory(companyID?: number): Observable<ResPackageCategory[]> {
    return this.packageApi.getPackageCategory(companyID);
  }

  getSupplierEditRequestDetails(params: IResApiSupplierEditRequest): Observable<ResSupplierEditRequestDetails[]> {
    return this.bookingApi.getSupplierEditRequestDetails(params);
  }

  manageSupplierItemRequest(params?: IResApiManageSupplierItemRequestListItem) {
    return this.bookingApi.manageSupplierItemRequest(params);
  }

  getGoodwillDepositParams(): Observable<{ RMTypes: ResGoodwillRMTypes[], pricesAndCurrencies: ResGoodwillDefaultPrices[], priceTypes: ResGoodwillDefaultPriceType[], companies: ResGoodwillCompany[] }> {
    return this.cashApi.getGoodwillDepositParams();
  }

  getHeritageDepositParams(params?: IResApiGetHeritageDepositParams): Observable<{ priceTypes: ResHeritageTier[], currencies: ResHeritageCurrency[], prices: ResHeritagePrice[] }> {
    return this.cashApi.getHeritageDepositParams(params);
  }

  // getContinent(): Observable<ResContinent[]> {
  //   return this.getDynamicAPI('pkg/getContinent').pipe(
  //     map<DynamicApiResponse, ResContinent[]>((response) => {
  //       return response.body.map((data: any) => new ResContinent(data))
  //     }),
  //   )
  // }

  manageJetSeats(params: IResApiManageJetSeats) {
    return this.bookingApi.manageJetSeats(params);
  }

  getExchangeRate(baseCurrency: string): Observable<ResPriceSetupExchangeRate[]> {
    return this.productPriceApi.getExchangeRate(baseCurrency);
  }

  getPackageItinerary(voyageID: number): Observable<ResPackageItinerary> {
    return this.productPriceApi.getPackageItinerary(voyageID);
  }

  getPackageTravelDateRelease(packageID: number, packageTravelDateID: number): Observable<{ status: string, releaseData: ResPackageTravelDateRelease[] }> {
    return this.packageApi.getPackageTravelDateRelease(packageID, packageTravelDateID);
  }

  managePackageTravelDateRelease(params: IResApiManagePackageTravelDateRelease) {
    return this.packageApi.managePackageTravelDateRelease(params);
  }

  packageSwap(params: IResApiPackageSwapParams) {
    return this.packageApi.packageSwap(params);
  }

  getPkgMasterSlave(params: IResApiGetPkgMasterSlaveParams): Observable<ResPackageMasterSlave[]> {
    return this.packageApi.getPkgMasterSlave(params);
  }

  getPkgSlave(params: IResApiGetPkgMasterSlaveParams): Observable<ResPackageMasterSlave[]> {
    return this.packageApi.getPkgSlave(params);
  }

  managePkgMasterSlave(params: IResApiManagePkgMasterSlaveParams) {
    return this.packageApi.managePkgMasterSlave(params);
  }

  getInventoryPackage(voyageID: number): Observable<ResInventoryPackage[]> {
    return this.inventoryApi.getInventoryPackage(voyageID);
  }

  changeBookingOwner(params: IResApiChangeBookingOwnerParams) {
    return this.bookingApi.changeBookingOwner(params);
  }

  getOandaCurrencyRoe() {
    return this.callDynamicAPI('oanda/roe', {}).pipe(
      map<DynamicApiResponse, ResOandaCurrency[]>((response) => {
        return response.body.map((data: any) => new ResOandaCurrency(data))
      })
    )
  }

  createIntraBooking(bkgID: number) {
    return this.bookingApi.createIntraBooking(bkgID);
  }

  getBkgIntercompanyList(bkgID: number): Observable<ResBkgIntercompanyList[]> {
    return this.bookingApi.getBkgIntercompanyList(bkgID);
  }

  intraBookingImport(params: IResApiIntraBookingImportParams) {
    return this.bookingApi.intraBookingImport(params);
  }

  // getBkgIntercompanyListRow(bkgID: number): Observable<ResBkgIntercompanyListRow[]> {
  //   return this.callDynamicAPI('booking/getSyncItems', {BkgID: bkgID}).pipe(
  //     map<DynamicApiResponse, ResBkgIntercompanyListRow[]>((response) => {
  //       return response.body.map((data: any) => new ResBkgIntercompanyListRow(data))
  //     })
  //   )
  // }

  // manageSyncIntercompanyBooking(params: IResApiManageSyncIntercompanyBookingParams) {
  //   return this.postDynamicAPI('booking/manageSyncItems', params).pipe(
  //     map<DynamicApiResponse, any>((response) => {
  //       return response.body;
  //     })
  //   )
  // }

  syncIntercompanyBooking(params: IResApiSyncIntercompanyBookingParams) {
    return this.bookingApi.syncIntercompanyBooking(params);
  }

  public getVoyageInventoryInfo(voyageID: number, endpoint: string = 'booking/getVoyageInventoryInfo') {
    return this.inventoryApi.getVoyageInventoryInfo(voyageID, endpoint);
  }

  getInventoryPriceTypes(voyageID: number): Observable<ResInventoryPriceType[]> {
    return this.inventoryApi.getInventoryPriceTypes(voyageID);
  }

  packageOptionCostBandGet(params: IResPackageCostBandGetParams) {
    return this.packageApi.packageOptionCostBandGet(params);
  }

  packageOptionCostBandManage(params: IResPackageCostBandManageParams) {
    return this.packageApi.packageOptionCostBandManage(params);
  }

  villaSellingAvailabilityGet(params: IResVillaAvailabilityGetParams) {
    return this.productPriceApi.villaSellingAvailabilityGet(params);
  }

  getBkgVillaService(bkgID: number): Observable<ResBkgVillaService[]> {
    return this.villaApi.getBkgVillaService(bkgID);
  }

  getBkgVillaOption(bkgID: number, serviceID: number): Observable<ResBkgVillaOption[]> {
    return this.villaApi.getBkgVillaOption(bkgID, serviceID);
  }

  addBkgVillaOption(params: IResApiAddVillaOptionParams) {
    return this.villaApi.addBkgVillaOption(params);
  }

  getCharterBookingParams(voyageID: number): Observable<ResBargeVoyageParams> {
    return this.inventoryApi.getCharterBookingParams(voyageID);
  }
  changeVilla(params: IResExtendBookingApiParams): Observable<IResBookingCreateApiResponse> {
    return this.postDynamicAPI('bkg/extend', params).pipe(
      map<DynamicApiResponse, IResBookingCreateApiResponse>((apiResponse) => {
        let response: IResBookingCreateApiResponse = {
          BkgID: ResBooking.INVALID_BOOKING
        };

        //TODO: check function Artiom
        //Exception only used in Heritage deposit page
        // if(apiResponse.body.DT0[0].BkgID) {
        //   response.BkgID = apiResponse.body.DT0[0].BkgID ?? ResBooking.INVALID_BOOKING;
        //   return response;
        // }

        if (!!apiResponse && Array.isArray(apiResponse.body) && apiResponse.body.length > 0) {
          response.BkgID = apiResponse.body[0].BkgID ?? ResBooking.INVALID_BOOKING;
          response.ErrorMessage = apiResponse.body[0].ErrorMessage;
        }

        return response;
      })
    );
  }

  getBkgJourneyItineraryHistoryDates(params: IResApiGetBkgJourneyItineraryHistoryParams): Observable<ResBkgJourneyItineraryHistoryDates[]> {
    return this.bookingApi.getBkgJourneyItineraryHistoryDates(params);
  }

  getBkgJourneyItineraryHistoryDetails(params: IResApiGetBkgJourneyItineraryHistoryDetailsParams): Observable<ResBkgJourneyItineraryHistory[]> {
    return this.bookingApi.getBkgJourneyItineraryHistoryDetails(params);
  }

  importBkgJourneyFromProposal(params: IResApiGetBkgJourneyItineraryHistoryParams) {
    return this.bookingApi.importBkgJourneyFromProposal(params);
  }

  confirmVilla(bkgID: number) {
    return this.bookingApi.confirmVilla(bkgID);
  }

  getPackagePhysicalLevels() {
    return this.packageApi.getPackagePhysicalLevels();
  }

  getOwnArrangement(OwnArrangementID: number) {
    return this.airApi.getOwnArrangement(OwnArrangementID);
  }

  manageOwnArrangement(params: IResApiManageOwnArrangementParams[]) {
    return this.airApi.manageOwnArrangement(params);
  }

  getAirlines({ AirLine, AirLineCod }: { AirLine?: string, AirLineCod?: string }) {
    return this.airApi.getAirlines({ AirLine, AirLineCod });
  }

  getAirlinesList(): Observable<ReservationApiAirLineList[]> {
    return this.serviceSetupApi.getAirlinesList();
  }

  getAirports({ IataCod, AirName }: { IataCod?: string, AirName?: string }) {
    return this.airApi.getAirports({ IataCod, AirName });
  }

  getExtraSegments() {
    return this.coreApi.getExtraSegments();
  }

  getServiceBookingReport(params: { ServiceID: number, ServiceOptionID?: number }): Observable<ReservationServiceBookingReport[]> {
    return this.serviceSetupApi.getServiceBookingReport(params);
  }
  getServicePackageList(params: { ServiceID: number, OptionID?: number }): Observable<ReservationServicePackageListElement[]> {
    return this.serviceSetupApi.getServicePackageList(params);
  }
  getServiceExperienceList(params: { ServiceID: number, OptionID?: number }): Observable<ReservationServiceExperienceListElement[]> {
    return this.serviceSetupApi.getServiceExperienceList(params);
  }

  getPackageServiceAllocations(params: { PackageID: number }): Observable<{ travelDates: { packageTravelDateID: number, travelStartDate: string, travelEndDate: string }[], options: ReservationPackageServiceAllocation[] }> {
    return this.packageApi.getPackageServiceAllocations(params);
  }

  getServiceAllocationForInspectModal(params: any): Observable<ReservationServiceAllocation[]> {
    return this.packageApi.getServiceAllocationForInspectModal(params);
  }

  managePackageServiceAllocations(params: any) {
    return this.packageApi.managePackageServiceAllocations(params);
  }

  getSupplierCancPolicy(params: { supplierID?: number, serviceID?: number, optionID?: number }): Observable<ResSupplierCancPolicy[]> {
    return this.supplierApi.getSupplierCancPolicy(params);
  }

  manageSupplierCancPolicy(params: any): Observable<any> {
    return this.supplierApi.manageSupplierCancPolicy(params);
  }

  getSupplierDepScheduling(params: { supplierID?: number, serviceID?: number, optionID?: number }): Observable<ResSupplierDepScheduling[]> {
    return this.supplierApi.getSupplierDepScheduling(params);
  }

  manageSupplierDepScheduling(params: any): Observable<any> {
    return this.supplierApi.manageSupplierDepScheduling(params);
  }

  getServiceTC(params: { supplierID?: number }): Observable<ReservationServiceTC[]> {
    return this.supplierApi.getServiceTC(params);
  }

  getPackageHighlightsByPackageType(): Observable<ReservationPackageHighlightByType[]> {
    return this.rescomApiService.getPackageHighlightsByPackageType();
  }
  managePackageHighlightsByPackageType(params: { PackageHighlightsByPackageType: ReservationPackageHighlightByType[] }): Observable<any> {
    return this.rescomApiService.managePackageHighlightsByPackageType(params);
  }
  getPackageHighlightsTypeList(params?: any, loadOptions?: any): Observable<{ data: ReservationPackageHighlightsType[]; totalCount: number }> {
    return this.rescomApiService.getPackageHighlightsTypeList(params, loadOptions);
  }
  getScopeHighlightsList(): Observable<{ Scope: string }[]> {
    return this.rescomApiService.getScopeHighlightsList();
  }
  managePackageHighlightsType(params: { PackageHighlightsType: ReservationPackageHighlightsType & { status: 'A' | 'D' } }): Observable<any> {
    return this.rescomApiService.managePackageHighlightsType(params);
  }
  manageConfirmSuiteStatus(params: IResBookingManageConfirmSuiteStatusParams): Observable<any> {
    return this.bookingApi.manageConfirmSuiteStatus(params);
  }

  manageSuiteRemark(params: IResBookingManageSuiteRemarkParams): Observable<any> {
    return this.inventoryApi.manageSuiteRemark(params);
  }

  getPriceSetupBookingList(params?: IResApiGetPriceSetupBookingListParams): Observable<ResPriceSetupBookingList[]> {
    return this.productPriceApi.getPriceSetupBookingList(params);
  }
  getPackageClearing(params: { hasCruise: boolean, gty?: boolean }): Observable<ResPackageClearingCruise[] | ResPackageClearingNoCruise[]> {
    return this.rescomApiService.getPackageClearing(params);
  }
  getPackageClearingByCompany(params: { companyID: number, gty?: boolean }): Observable<ResPackageClearingVoyage[]> {
    return this.rescomApiService.getPackageClearingByCompany(params);
  }
  getPackageClearingCompanyList(): Observable<{ companyID: number, endpoint?: string }[]> {
    return this.rescomApiService.getPackageClearingCompanyList();
  }
  editVillaPrice(params: IResVillaEditPrice) {
    return this.inventoryApi.editVillaPrice(params);
  }

  getExportInvoiceData(params?: IResApiGetExportInvoiceDataParams): Observable<ResAccountingExportData[]> {
    return this.bookingApi.getExportInvoiceData(params);
  }

  getPurchaseOrderForPackage(params: IResApiGetPurcahseOrderParams): Observable<ResPackagePurchaseOrder> {
    return this.packageApi.getPurchaseOrderForPackage(params);
  }

  getSanctuaryCampAllocations(filters?: IReservationApiGetSanctuaryCampAllocations, loadOptions?: any): Observable<{ data: ResSanctuaryCampAllocation[]; totalCount: number }> {
    return this.rescomApiService.getSanctuaryCampAllocations(filters, loadOptions);
  }

  getSanctuaryCampAllocationsDownload(params: IReservationApiGetSanctuaryCampAllocations): Observable<ResSanctuaryCampAllocation[]> {
    return this.rescomApiService.getSanctuaryCampAllocationsDownload(params);
  }

  getSanctuaryCampSuiteCategoryFilters(params?: { PropertyID: number }): Observable<ResSanctuaryCampSuiteCategory[]> {
    return this.rescomApiService.getSanctuaryCampSuiteCategoryFilters(params);
  }
  getSanctuaryCampAgenciesFilters(params?: { Agency?: string }): Observable<ResSanctuaryCampAgency[]> {
    return this.rescomApiService.getSanctuaryCampAgenciesFilters(params);
  }

  getPackageCostSupplierList(params: IResApiGetPackageCostSupplierListParams): Observable<ResPackageCostSupplierList[]> {
    return this.packageApi.getPackageCostSupplierList(params);
  }

  getBookingSupplierListForAdvisingReport(bookingID: number): Observable<ResBookingSupplierListForAdvisingReport[]> {
    return this.supplierApi.getBookingSupplierListForAdvisingReport(bookingID);
  }

  manageRequestAdvisingReport(params: IResBookingSupplierListForAdvisingReportParams): Observable<any> {
    return this.supplierApi.manageRequestAdvisingReport(params);
  }

  getFutureFlights(bookingID: number): Observable<ResFutureFlights> {
    return this.callDynamicAPI('bookingAir/getFutureFlight', { BkgID: bookingID }).pipe(
      map<DynamicApiResponse, ResFutureFlights>((response) => {
        const futureFlights = new ResFutureFlights(response.body?.DT0[0])
        futureFlights.futureFlightsDetail = response.body?.DT1.map((detail: any) => new FutureFlightsDetail(detail))
        futureFlights.futureFlightsGuest = response.body?.DT2.map((detail: any) => new FutureFlightsGuest(detail))
        return futureFlights
      })
    );
  }

  manageFutureFlights(params?: IResApiFutureFlights) {
    return this.bookingApi.manageFutureFlights(params);
  }

  managePackageSuiteCategoryStatus(params: IResApiManagePackageSuiteCategoryStatus): Observable<any> {
    return this.packageApi.managePackageSuiteCategoryStatus(params);
  }

  getDayByDayPackages(params: IResApiGetDayByDayPackagesParams): Observable<ResBookingPackageDayByDay[]> {
    return this.bookingApi.getDayByDayPackages(params);
  }

  manageDayByDayPackages(params: IResApiManageDayByDayPackagesParams): Observable<any> {
    return this.bookingApi.manageDayByDayPackages(params);
  }

  getDayByDayTopicItinerary(params: IResApiGetDayByDayTopicItineraryParams): Observable<ResBookingTopicDayByDay[]> {
    return this.bookingApi.getDayByDayTopicItinerary(params);
  }

  getDayByDayNotesItinerary(bkgID: number): Observable<ResBookingNotesDayByDay[]> {
    return this.bookingApi.getDayByDayNotesItinerary(bkgID);
  }

  getDayByDaySuggestedNotes(bkgID: number): Observable<ResBookingDayByDayNotesSuggestion[]> {
    return this.bookingApi.getDayByDaySuggestedNotes(bkgID);
  }

  getDayByDayInclusions(bkgID: number, dayByDayTypeID = 1): Observable<ResBookingInclusionsDayByDay[]> {
    return this.bookingApi.getDayByDayInclusions(bkgID, dayByDayTypeID);
  }

  manageDayByDayInclusions(params: IResApiManageDayByDayInclusionsParams): Observable<any> {
    return this.bookingApi.manageDayByDayInclusions(params);
  }

  manageSyncDayByDayInclusions(bkgID: number, dayByDayTypeID = 1): Observable<any> {
    return this.bookingApi.manageSyncDayByDayInclusions(bkgID, dayByDayTypeID);
  }

  manageDayByDayNotesItinerary(params: IResApiManageDayByDayNotesItinerary): Observable<any> {
    return this.bookingApi.manageDayByDayNotesItinerary(params);
  }

  manageDayByDayTopicItinerary(params: IResApiManageDayByDayTopicItinerary): Observable<any> {
    return this.bookingApi.manageDayByDayTopicItinerary(params);
  }

  resetDayByDayTopicItinerary(bkgID: number): Observable<any> {
    return this.bookingApi.resetDayByDayTopicItinerary(bkgID);
  }

  restoreCommissionFromTam(bkgID: number): Observable<any> {
    return this.commissionApi.restoreCommissionFromTam(bkgID);
  }

  getTopicPackage(params: IResApiGetDayByDayTopicItineraryParams): Observable<ResPackageTopic[]> {
    return this.packageApi.getTopicPackage(params);
  }

  manageTopicPackage(params: IResApiManageTopicPackageParams): Observable<any> {
    return this.packageApi.manageTopicPackage(params);
  }

  resetTopicPackage(packageID: number): Observable<any> {
    return this.packageApi.resetTopicPackage(packageID);
  }

  getDayByDayTopicHistory(bkgID: number): Observable<ResBkgTopicHistoryDates[]> {
    return this.bookingApi.getDayByDayTopicHistory(bkgID);
  }

  getDayByDayTopicHistoryDetail(params: IResApiGetBkgTopicHistoryDetailsParams): Observable<ResBkgTopicHistoryDetail[]> {
    return this.bookingApi.getDayByDayTopicHistoryDetail(params);
  }

  deleteFutureFlight(params: IReservationApiDeleteFutureFlightParams): Observable<any> {
    return this.airApi.deleteFutureFlight(params);
  }

  getBkgDiscountAnalysys(params: IResApiGetBkgDiscountAnalysysParams): Observable<{ discountAnalysys: ResBkgDiscountAnalysys[], discountGroups: ResBkgDiscountAnalysysGroup[] }> {
    return this.bookingApi.getBkgDiscountAnalysys(params);
  }

  getBookingBrands(): Observable<{ brandID: number, brandName: string }[]> {
    return this.bookingApi.getBookingBrands();
  }

  getExportItemBySuppliersExcel(bkgID: number, groupBy: string, pageSize: number): Observable<any> {
    return this.bookingApi.getExportItemBySuppliersExcel(bkgID, groupBy, pageSize);
  }

  getDayByDayImages(params: IResApiGetDayByDayImagesParams): Observable<ResBookingDayByDayImages[]> {
    return this.bookingApi.getDayByDayImages(params);
  }

  getDayByDayAllImagesByCategory(params: IResApiGetDayByDayAllImagesByCategoryParams): Observable<ResBookingDayByDayAllImages[]> {
    return this.bookingApi.getDayByDayAllImagesByCategory(params);
  }

  manageDayByDayImages(params: IResApiManageDayByDayImagesParams): Observable<any> {
    return this.bookingApi.manageDayByDayImages(params);
  }

  getBkgCommunicationEmail(bkgID: number): Observable<ResBkgCommunicationEmail[]> {
    return this.bookingApi.getBkgCommunicationEmail(bkgID);
  }

  getBkgMultiVoyages(bkgID: number): Observable<ResBookingMultiVoyage[]> {
    return this.bookingApi.getBkgMultiVoyages(bkgID);
  }
  pinMenuItem(menuID: number, pin: boolean) {
    var params = {
      HighlightsMenuID: menuID,
      ActionTypeID: pin ? 1 : 2
    }
    return this.postDynamicAPI('menu/pinItem', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getBkgCommunicationEmailTemplate(templateID: string): Observable<ResBkgCommunicationEmailTemplate> {
    return this.bookingApi.getBkgCommunicationEmailTemplate(templateID);
  }

  getBkgCommunicationEmailConfiguration(configurationCod: string): Observable<string> {
    return this.bookingApi.getBkgCommunicationEmailConfiguration(configurationCod);
  }

  getPackageOperationSpecialist(): Observable<ResPackageOperationSpecialist[]> {
    return this.packageApi.getPackageOperationSpecialist();
  }

  getAccomodationReport(packageID: number): Observable<any> {
    return this.packageApi.getAccomodationReport(packageID);
  }

  getPackageReport(params: IResApiGetPackageReportParams): Observable<any> {
    return this.packageApi.getPackageReport(params);
  }

  getPackageChangesHistory(packageID: number): Observable<ResPackageChangesHistory[]> {
    return this.packageApi.getPackageChangesHistory(packageID);
  }

  getPackageShipSetup(params: IResApiGetPackageShipSetupParams): Observable<{
    packageShipSetup: ResPackageShipSetup[],
    voyages: { voyageID: number, voyageNumber: string }[],
    suites: { suiteID: number, suiteNo: string, suiteNumber: string }[],
    suiteCategories: { suiteCategoryID: number, suiteCategoryCod: string, suiteCategory: string }[]
  }> {
    return this.packageApi.getPackageShipSetup(params);
  }


  managePackageShipSetup(params: IResApiManagePackageShipSetupParams): Observable<any> {
    return this.packageApi.managePackageShipSetup(params);
  }

  manageManualFlights(params?: IResApiManualFlights) {
    return this.bookingApi.manageManualFlights(params);
  }

  manageInternalFlights(params?: IResApiManualFlights) {
    return this.bookingApi.manageInternalFlights(params);
  }

  getCircuits(filters?: { [key: string]: unknown }, loadOptions?: any): Observable<{ data: Circuit[]; totalCount: number }> {
    return this.rescomApiService.getCircuits(filters, loadOptions);
  }

  manageCircuit(params: Partial<ICircuitManage>): Observable<Circuit> {
    return this.rescomApiService.manageCircuit(params);
  }
  getCircuitDetail(circuitID: number): Observable<Circuit> {
    return this.rescomApiService.getCircuitDetail(circuitID);
  }

  getCircuitType(): Observable<CircuitType[]> {
    return this.rescomApiService.getCircuitType();
  }
  getCircuitLookup(): Observable<CircuitLookupItem[]> {
    return this.rescomApiService.getCircuitLookup();
  }
  getCircuitDiscountToService(circuitDiscountID: number): Observable<CircuitDiscountToService[]> {
    return this.callDynamicAPI('circuit/getCircuitDiscountToService', {CircuitDiscountID: circuitDiscountID}).pipe(
      map<DynamicApiResponse, CircuitDiscountToService[]>((response) => response.body?.map((data: any) => new CircuitDiscountToService(data))));
  }
  manageCircuitDiscountToService(params: ICircuitDiscountToServiceManage): Observable<unknown> {
    return this.callDynamicAPI('circuit/manageCircuitDiscountToService', params, 'POST').pipe(
      map<DynamicApiResponse, Circuit>((response) => {
        return response.body;
      }));
  }

  getManifestPackageServiceTypes(): Observable<{ serviceTypeID: number, serviceTypeName: string, productCod: string }[]> {
    return this.manifestApi.getManifestPackageServiceTypes();
  }

  getSavedFlights(params: IResApiGetAirFlightParams): Observable<ResAirInternalFlight[]> {
    return this.bookingApi.getSavedFlights(params);
  }

  getFlightServiceOption(bkgID: number): Observable<ResAirServiceOption[]> {
    return this.airApi.getFlightServiceOption(bkgID);
  }

  // getSavedFlights(params: IResApiGetAirFlightParams): Observable<{flights: ResAirInternalFlight[], serviceOptions: ResAirServiceOption[]}> {
  //   return this.callDynamicAPI('air/getInternalFlight', params).pipe(
  //     map<DynamicApiResponse, {flights: ResAirInternalFlight[], serviceOptions: ResAirServiceOption[]}>((response) => {
  //       const header = response.body.DT0 ?? []
  //       const details = response.body.DT1 ?? []
  //       const serviceOptions = response.body.DT2 ?? []

  //       return header.map((v: any) => {
  //         v.Details = details.filter((d: any) => d.FlightHeaderID === v.FlightHeaderID)
  //         return {
  //           flights: header.map((h: any) => new ResAirInternalFlight(h)),
  //           serviceOptions: serviceOptions.map((s: any) => new ResAirServiceOption(s))
  //         }
  //       }
  //       )
  //     })
  //   )
  // }

  getSavedFutureFlights(params: IResApiGetAirFlightParams): Observable<ResAirFutureFlight[]> {
    return this.bookingApi.getSavedFutureFlights(params);
  }

  getPriceSetups(params?: Partial<{ PriceSetUpID: number, CostSetup: 'Y' | 'N', PageNum: number, PageSize: number }>): Observable<IReservationPriceSetup[]> {
    return this.productPriceApi.getPriceSetups(params);
  }

  getAgencyGroups(companyID: number): Observable<{Cod:string, Desc:string}[]> {
    return this.callDynamicAPI('product/getAgencyGroups', {CompanyID: companyID}, 'GET').pipe(
      map<DynamicApiResponse, any[]>((response) => <any[]>(response.body ?? [])));
  }

  getLkAgencyGroups(companyCode: string): Observable<{ Cod: string; Desc: string }[]> {
    return this.callDynamicAPI('LKAgency/Group', { CompanyCode: companyCode }, 'GET').pipe(
      map<DynamicApiResponse, { Cod: string; Desc: string }[]>((response) => response.body ?? [])
    );
  }

  getBookingLanguage(): Observable<ResBookingLanguage[]> {
    return this.bookingApi.getBookingLanguage();
  }

  getEresCalendarAvailability(params: IResApiGetEresCalendarAvailabilityParams): Observable<ResBookingEresCalendarAvailability> {
    return this.productPriceApi.getEresCalendarAvailability(params);
  }

  getEresSearchAvailabilityCamps(): Observable<ResBookingEresSearchCamp[]> {
    return this.productPriceApi.getEresSearchAvailabilityCamps();
  }
  bkgSaveAsTemplate(params: IResApiBkgSaveAsTemplate): Observable<any> {
    return this.bookingApi.bkgSaveAsTemplate(params);
  }
  getBkgTemplateAllTags(): Observable<ResBkgTemplateTag[]> {
    return this.bookingApi.getBkgTemplateAllTags();
  }
  getTemplateItems(templateID: number): Observable<ResBkgTemplate> {
    return this.productPriceApi.getTemplateItems(templateID);
  }

  bkgAddTemplateItems(params: IResApiBkgAddTemplateItems): Observable<any> {
    return this.productPriceApi.bkgAddTemplateItems(params);
  }

  bkgAddMarginOverlay(params: IResApiBkgAddMarginOverlay): Observable<any> {

    return this.callDynamicAPI('margin/overlayManage', params, 'POST').pipe(
      map<DynamicApiResponse, any>(response => response.body));
  }
  getServiceOptionDayByDay(params: IResApiGetServiceOptionDayByDayParams): Observable<ResServiceOptionDayByDay[]> {
    return this.serviceSetupApi.getServiceOptionDayByDay(params);
  }

  manageServiceOptionDayByDay(params?: IResApiManageServiceOptionDayByDayParams): Observable<any> {
    return this.serviceSetupApi.manageServiceOptionDayByDay(params);
  }

  getServiceConnectedList(optionID: number): Observable<ResServiceConnectedList[]> {
    return this.serviceSetupApi.getServiceConnectedList(optionID);
  }

  getBkgPromoDMC(params: IResApiGetPromoDMCParams): Observable<ResBkgPromoDMC[]> {
    return this.bookingApi.getBkgPromoDMC(params);
  }

  manageCircuitDiscount(params?: IResApiGetPromoDMCParams): Observable<any> {
    return this.bookingApi.manageCircuitDiscount(params);
  }

  getPackageTags(): Observable<ResPackageTags[]> {
    return this.packageApi.getPackageTags();
  }

  getPackageTag(packageID: number): Observable<ResPackageTagItem[]> {
    return this.packageApi.getPackageTag(packageID);
  }

  managePackageTag(params?: IResApiManagePackageTagParams): Observable<any> {
    return this.packageApi.managePackageTag(params);
  }

  getVersionHistory(bkgID: number): Observable<ResBkgVersionHistory[]> {
    return this.bookingApi.getVersionHistory(bkgID);
  }

  getOperationalDocument(params: IResApiGetOperationalDocumentParams): Observable<{ operational: ResBkgOperationalDocument[], serviceOptions: ResBkgOperationalDocServiceOption[], guests: ResBkgOperationalDocGuest[] }> {
    return this.serviceSetupApi.getOperationalDocument(params);
  }

  manageOperationalDocument(params: IResApiManageOperationalDocumentParams): Observable<any> {
    return this.serviceSetupApi.manageOperationalDocument(params);
  }

  getPackageMiniPackages(params: IResApiGetPackageMiniPackagesParams): Observable<ResMiniPackage[]> {
    return this.packageApi.getPackageMiniPackages(params);
  }

  manageMiniPackage(params: IResApiManageMiniPackageParams): Observable<any> {
    return this.packageApi.manageMiniPackage(params);
  }

  getBookingPayerManagement(bkgID: number): Observable<{ payers: ResBookingPayerMngmtPayers[], groups: ResBookingPayerMngmtGroups[], guests: ResBookingPayerMngmtGuests[], relations: ResBookingPayerMngmtRelations[], roles: ResBookingPayerMngmtRoles[] }> {
    return this.bookingApi.getBookingPayerManagement(bkgID);
  }
  getBookingPayerByGroup(bkgID: number, paxGroupID: number): Observable<ResBookingPayerMngmtPayers[]> {
    return this.callDynamicAPI('payment/getPayerManagement', { BkgID: bkgID, OnlyActivePayer: true, PaxGroupID: paxGroupID }).pipe(
      map<DynamicApiResponse, ResBookingPayerMngmtPayers[]>((response) => {
        return response.body.map((data: any) => (new ResBookingPayerMngmtPayers(data)));
      })
    );
  }
  getBookingPaymentDetailsMappingData(body:any) {
    return this.cashApi.getBookingPaymentDetailsMappingData(body);
  }
  getBookingPaymentDetailsPayers(orderID: string, requestPaymentActionID:number): Observable<ResBookingPaymentDetailPayer[]> {
    return this.cashApi.getBookingPaymentDetailsPayers(orderID, requestPaymentActionID);
  }
  getBookingPaymentDetails(orderID: string): Observable<ResBookingPaymentDetails> {
    return this.callDynamicAPI('payment/getRequestDetails', { OrderID: orderID }).pipe(
      map<DynamicApiResponse, ResBookingPaymentDetails>((response) => {
        const data = this.getBookingPaymentDetailsMappingData(response.body)
        return new ResBookingPaymentDetails(data)
      })
    );
  }
  getBookingPayerDetails(bkgID: number, payerID:number, guestID: number, onlyWithPrice = true): Observable<ResBookingPayerDetails[]> {
    return this.bookingApi.getBookingPayerDetails(bkgID, payerID, guestID, onlyWithPrice);
  }
  getBookingPayerGroups(bkgID: number): Observable<ResBookingPayerGroup[]> {
    return this.callDynamicAPI('payment/GroupDefaultPayer', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBookingPayerGroup[]>((response) => {
        return response.body.map((data: any) => (new ResBookingPayerGroup(data)));
      })
    );
  }
  manageBookingPayerManagement(params: IResApiManageBookingPayerManagementParams): Observable<ResMaganeBookingPayerResponse[]> {
    return this.bookingApi.manageBookingPayerManagement(params);
  }

  getInvetoryVoyagePackages(voyageID: number): Observable<ResInventoryVoyagePackage[]> {
    return this.inventoryApi.getInvetoryVoyagePackages(voyageID);
  }

  getSyncMiniPackage(bkgID?: number): Observable<any> {
    return this.bookingApi.getSyncMiniPackage(bkgID);
  }

  getBkgAdditionalExp(bkgID: number, experienceID: number): Observable<ResBkgExperienceAdditional[]> {
    return this.bookingApi.getBkgAdditionalExp(bkgID, experienceID);
  }

  manageBkgAdditionalExp(params: IResApiManageBkgAdditionalExpParams): Observable<any> {
    return this.serviceSetupApi.manageBkgAdditionalExp(params);
  }

  getBkgIncludedItems(bkgID: number): Observable<ResBkgIncludedItems[]> {
    return this.bookingApi.getBkgIncludedItems(bkgID);
  }

  manageBkgIncludedItems(params: IResApiManageBkgIncludedItemsParams): Observable<any> {
    return this.bookingApi.manageBkgIncludedItems(params);
  }

  getBkgAllowanceItems(bkgID: number): Observable<ResBkgAllowanceItems[]> {
    return this.bookingApi.getBkgAllowanceItems(bkgID);
  }

  manageBkgAllowanceItems(params: IResApiManageBkgIncludedItemsParams): Observable<any> {
    return this.bookingApi.manageBkgAllowanceItems(params);
  }

  getMiniPackageBkgList(bkgID: number): Observable<ResMiniPackageBkgList[]> {
    return this.bookingApi.getMiniPackageBkgList(bkgID);
  }

  syncPkgECM(params: IResApiSyncPkgECMParams): Observable<any> {
    return this.packageApi.syncPkgECM(params);
  }

  resetDayByDayImages(params: IResApiResetDayByDayImagesParams): Observable<any> {
    return this.bookingApi.resetDayByDayImages(params);
  }

  getDMCDocCheck(bkgID: number, fullCheck = false): Observable<ResDMCDocCheck[]> {
    return this.bookingApi.getDMCDocCheck(bkgID, fullCheck);
  }

  sendDOCAutoInfo(bkgID: number): Observable<any> {
    return this.bookingApi.sendDOCAutoInfo(bkgID);
  }
  managePayerDetailItems(params: IResPayerItemsManageParams): Observable<any> {
    return this.bookingApi.managePayerDetailItems(params);
  }

  getBkgLoyaltyItems(bkgID: number): Observable<{ redeemItems: ResBkgLoyaltyRedeemItems[], availableItems: ResBkgLoyaltyAvailableItems[] }> {
    return this.peopleApi.getBkgLoyaltyItems(bkgID);
  }

  manageBkgLoyaltyItems(params: IResApiManageBkgLoyaltyItemsParams): Observable<any> {
    return this.peopleApi.manageBkgLoyaltyItems(params);
  }

  getEresIntegrationCheck(bkgID: number): Observable<ResEresIntegrationCheck[]> {
    return this.bookingApi.getEresIntegrationCheck(bkgID);
  }

  getPackageInvoices(params: IResApiGetPackageInvoicesParams): Observable<IResPackageInvoicesResult> {
    return this.packageApi.getPackageInvoices(params);
  }

  managePeriodCostPerDay(params: any): Observable<any> { //TODO INTERFACE
    return this.postDynamicAPI('product/managePeriodCostPerDay', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getPackageDmcPrice(params: IResApiGetPackageDmcPriceParams): Observable<ResPackageDmcPriceResult> {
    return this.packageApi.getPackageDmcPrice(params);
  }

  managePackageDmcPrice(params: IResApiManagePackageDmcPriceParams): Observable<any> {
    return this.packageApi.managePackageDmcPrice(params);
  }
}
